import jsPDF from 'jspdf';
import autoTable, { FontStyle } from 'jspdf-autotable';
import type { Property, Comp, ValuationResult, DealAnalysis } from '../types';
import {
  calculateVacancyAmount,
  calculateEffectiveGrossIncome,
  calculateManagementFee,
  calculateTotalExpenses,
  calculateCapRate,
  calculateLoanAmount,
  calculateMonthlyPayment,
  calculateAnnualDebtService,
  calculateDSCR,
  calculateClosingCosts,
  calculateTotalCashRequired,
  calculateBeforeTaxCashFlow,
  calculateCashOnCash,
  calculateSaleProceeds,
  calculateIRR,
  calculateEquityMultiple,
  calculateAvgCashOnCash,
  buildIRRCashFlows,
  generateProjections,
  STRATEGY_THRESHOLDS,
  resolveExpenseRecoveries,
  calculateAdjustedDownPayment,
  computeScenario,
  sizeLoanFromEquity,
  solveMaxPurchasePrice,
  solveRequiredNOILift,
  solveRequiredRentPSF,
  solveCapexCeiling,
  solveTargetExitCap,
} from './financialCalculations';
import { resolveThresholdLabel } from './archetypePresets';
import { scoreFinding } from './scoreFinding';
import { formatFeeRate } from './managementFee';
import type { InvestmentStrategy, ScenarioInputs, LoanSizingResult, StrategyThresholds } from './financialCalculations';
import { exitNoiYearLabel, yearOneNoi, finalHoldYearNoi, inPlaceNoi, noiProjectionInputs, capBasisCaption, type ExitNoiResult } from './scheduleAnchors';
import { runScenario } from './scenarioLevers';
import { resolveFinancingFee } from './financingFee';
import { secondaryNoteAnnualDebtService, type SecondaryNote } from './financialCalculations';
import { verdictFrom, isAmberVerdict, VERDICT_DEFINITIONS, type ScoreRow, type Verdict } from '../components/analysis/scoreDeal';
import {
  CompanyBranding,
  MANCHESTER_PDF,
  registerAnalysisFont,
  drawMasthead,
  drawLogoRow,
  drawFooterLine,
  drawAnalysisSectionTitle,
  analysisTableStyles,
  loadLogoImage,
  PREPARED_BY,
  FIRM_ADDRESS_LINE,
  exportFileName,
} from './pdfBranding';
import { fileExportedPdf, type ExportOutcome } from './exportFiling';

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined || !isFinite(value)) return 'N/A';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const formatPercent = (value?: number | null): string => {
  if (value === null || value === undefined || !isFinite(value)) return 'N/A';
  return `${value.toFixed(2)}%`;
};

const formatNumber = (value?: number | null): string => {
  if (value === null || value === undefined || !isFinite(value)) return 'N/A';
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/** "$3.0M", "$725K" - the compact form his header band uses. */
const compactMoney = (v?: number | null): string => {
  if (v === null || v === undefined || !isFinite(v) || v === 0) return 'N/A';
  return v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M` : `$${Math.round(v / 1000)}K`;
};

/** "1.36x". Local like the other three - this file formats for print, not for the screen. */
const formatRatio = (value?: number | null): string => {
  if (value === null || value === undefined || !isFinite(value)) return 'N/A';
  return `${value.toFixed(2)}x`;
};

// ============================================================================
// Executive Summary PDF
// ============================================================================

/** The deal a property remembers (`additional_data.crm_deal_id`) - the backend resolves it too; sending it saves a lookup. */
const dealIdOf = (property: Property): string | null => {
  const extra = (property as { additional_data?: Record<string, unknown> | null }).additional_data;
  const id = extra?.crm_deal_id;
  return typeof id === 'string' && id ? id : null;
};

interface ExecSummaryOptions {
  property: Property;
  valuation: ValuationResult;
  branding?: CompanyBranding;
}

/**
 * The valuation summary the property page downloads - the Manchester chrome since 2026-09-09 (LB8):
 * Carlito, the masthead on every page with the marks under it on page 1, VALUATION SUMMARY in red,
 * navy section titles, navy-head zebra tables, the footer line. Letter, like every delivered document.
 */
export async function generateValuationSummaryPDF(options: ExecSummaryOptions): Promise<ExportOutcome> {
  const { property, valuation, branding } = options;

  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const frame = { pageWidth, pageHeight, margin };
  const c = MANCHESTER_PDF;

  const family = await registerAnalysisFont(doc);
  const [apexMark, kwMark] = await Promise.all([loadLogoImage('/apex-logo.png'), loadLogoImage('/kw-square.png')]);
  const tableStyles = analysisTableStyles(family);
  const table = (opts: Parameters<typeof autoTable>[1]) => autoTable(doc, {
    ...opts,
    styles: { ...tableStyles.styles, ...(opts.styles ?? {}) },
    headStyles: { ...tableStyles.headStyles, ...(opts.headStyles ?? {}) },
    alternateRowStyles: { ...tableStyles.alternateRowStyles, ...(opts.alternateRowStyles ?? {}) },
  });
  const firmName = branding?.company_name || 'Apex Real Estate Services';
  const TOP = 17;
  const PAGE1_TOP = 32;
  const section = (title: string, y: number): number => {
    if (y > 250) { doc.addPage(); y = TOP; }
    return drawAnalysisSectionTitle(doc, family, frame, title, y);
  };

  let yPos = PAGE1_TOP;
  doc.setTextColor(...c.red);
  doc.setFontSize(22);
  doc.setFont(family, 'bold');
  doc.text('VALUATION SUMMARY', margin, yPos);
  yPos += 4;
  doc.setDrawColor(...c.red);
  doc.setLineWidth(1.2);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  doc.setFontSize(13);
  doc.setFont(family, 'bold');
  doc.setTextColor(...c.navy);
  doc.text(property.address || 'Property Address', margin, yPos);
  yPos += 5;
  doc.setFontSize(9);
  doc.setFont(family, 'normal');
  doc.setTextColor(...c.muted);
  doc.text(`${property.city || ''}, ${property.state || ''} ${property.zip_code || ''}`, margin, yPos);
  yPos += 8;

  table({
    startY: yPos,
    head: [['Item', { content: 'Value', styles: { halign: 'right' } }]],
    body: [
      ['Estimated Value', formatCurrency(valuation.estimated_value)],
      ['Value Range', `${formatCurrency(valuation.value_range?.low)} - ${formatCurrency(valuation.value_range?.high)}`],
      ['Confidence', valuation.confidence_level],
      ['Building Size', property.building_size ? `${formatNumber(property.building_size)} SF` : 'N/A'],
      ['List Price', formatCurrency(property.price)],
      ['CAP Rate', property.cap_rate ? `${property.cap_rate}%` : 'N/A'],
    ],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: contentWidth * 0.45 },
      1: { cellWidth: contentWidth * 0.55, halign: 'right' },
    },
    margin: { left: margin, right: margin, top: TOP },
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;

  if (valuation.executive_summary) {
    yPos = section('SUMMARY', yPos);
    doc.setFontSize(9.5);
    doc.setFont(family, 'normal');
    doc.setTextColor(...c.ink);
    const lines: string[] = doc.splitTextToSize(valuation.executive_summary, contentWidth);
    doc.text(lines, margin, yPos);
    yPos += lines.length * 4.6 + 8;
  }

  if (valuation.pricing_scenarios) {
    yPos = section('PRICING SCENARIOS', yPos);
    const scenarios = valuation.pricing_scenarios;
    table({
      startY: yPos,
      head: [['Scenario', 'Price', 'Price / SF', 'Timeline']],
      body: [
        ['Quick Sale', formatCurrency(scenarios.quick_sale.price), `$${scenarios.quick_sale.price_per_sqft}/SF`, scenarios.quick_sale.timeline],
        ['Market Sale', formatCurrency(scenarios.market_sale.price), `$${scenarios.market_sale.price_per_sqft}/SF`, scenarios.market_sale.timeline],
        ['Premium Sale', formatCurrency(scenarios.premium_sale.price), `$${scenarios.premium_sale.price_per_sqft}/SF`, scenarios.premium_sale.timeline],
      ],
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 3 },
      margin: { left: margin, right: margin, top: TOP },
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  if (valuation.key_findings && valuation.key_findings.length > 0) {
    yPos = section('KEY FINDINGS', yPos);
    doc.setFontSize(9);
    doc.setFont(family, 'normal');
    doc.setTextColor(...c.ink);
    valuation.key_findings.forEach((finding, i) => {
      if (yPos > 262) { doc.addPage(); yPos = TOP; }
      const lines: string[] = doc.splitTextToSize(`${i + 1}. ${finding}`, contentWidth - 5);
      doc.text(lines, margin + 2, yPos);
      yPos += lines.length * 4.4 + 3;
    });
  }

  const footerLeft = `${firmName}  |  ${FOOTER_CONTACTS}  |  Confidential`;
  const dateLine = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawMasthead(doc, family, frame, firmName, dateLine);
    drawFooterLine(doc, family, frame, footerLeft, i);
  }
  doc.setPage(1);
  drawLogoRow(doc, family, frame, c.barHeight + 3, { apex: apexMark, kw: kwMark }, 'Confidential Valuation Summary');

  // Saved to his machine FIRST, then filed - the download never waits on, or fails with, the filing.
  const fileName = exportFileName('Valuation Summary', property.address);
  doc.save(fileName);
  return fileExportedPdf(doc, { kind: 'Valuation Summary', fileName, propertyId: property.id, dealId: dealIdOf(property) });
}
// ============================================================================
// Investment Analysis PDF - delivered corpus layout
//
// The layout here reproduces the analyses already delivered to clients (see
// the AutoZone/Dollar Tree Victorville and El Paseo Collection Elegante PDFs):
// red header band, INVESTMENT ANALYSIS title, address and deal name, the
// descriptor line, verdict box, five-metric hero strip, then the section run
// Income & Expense -> Operating Expenses -> Financing -> Loan Terms -> Rent
// Roll -> Proforma -> Exit -> Scorecard -> Sensitivity -> Key Considerations.
// ============================================================================

interface AIInsight {
  content: string;
  created_at: string;
}

interface InvestmentAnalysisPDFOptions {
  property: Property;
  data: Partial<DealAnalysis>;
  comps?: Comp[];
  branding?: CompanyBranding;
  photoBase64?: string | null;
  aiInsights?: AIInsight[];
  keyConsiderations?: string;
  // ── Supplied by InvestmentAnalysis.tsx ──────────────────────────────────
  // The export must report the same figures the screen just showed the
  // client. Recomputing them here from a different set of inputs is exactly
  // how a PDF ends up quoting a different IRR than the page it came from.
  scoreRows?: ScoreRow[];
  verdict?: Verdict;
  passed?: number;
  total?: number;
  profileName?: string;
  structureLine?: string;
  caveat?: string | null;
  descriptor?: string;
  sizing?: LoanSizingResult;
  /**
   * Contractual NOI by year, index 0 = year 1. Omitting this reverts the
   * export to growth-rate NOI while the screen uses the lease schedule, and
   * the two then disagree on IRR for the same deal.
   */
  noiOverrides?: Array<number | null>;
  /** Phase 3B leasing capital by year. The printed cash flow must match the screen's. */
  capitalCostsByYear?: number[];
  /** The exit-year NOI the screen priced its exit on. */
  exitNOI?: number;
  /**
   * ⭐⭐⭐ KK2. THE SCREEN'S RESOLVED SCENARIO BASE - `scenarioBaseResolved`, the one object every
   * on-screen scenario consumer reads: sized LTV, exit NOI, contractual schedule, leasing capital,
   * the SECOND NOTE (serviced and retired), the FINANCING FEE and a STATED EXIT VALUE. The document
   * used to build its own with the deprecated payment-only carryback and none of the last three,
   * so its sensitivity table and scenario page priced a deal the screen was not showing. Required:
   * a document that cannot be handed the base has no honest way to print one.
   */
  scenarioBase: ScenarioInputs;
  /**
   * WHICH year that NOI is, as `exitNoi` decided it. The caption differs by source and the PDF
   * cannot re-derive it, so restating "Year N+1" here printed the right figure under the wrong
   * year on every deal with an applied schedule. Defaults to the grown case, the old behaviour.
   */
  exitNoiSource?: ExitNoiResult['source'];
  /**
   * The bar the screen actually scored against. When an investor profile is
   * selected it outranks investment_strategy, so recomputing
   * STRATEGY_THRESHOLDS[strategy] here prints a different verdict than the page
   * the client just looked at. Omitting this falls back to the old behaviour.
   */
  thresholds?: StrategyThresholds;
  /** What the screen called that bar - profile name, else "<Strategy> Strategy". */
  thresholdLabel?: string;
  /** Material items outstanding. An EMPTY list is the second half of the GO definition, so the
   *  PDF cannot tell GO from CONDITIONAL without it. Printed under the scorecard. */
  openItems?: string[];
  /** One clause appended to the computed finding on page 1 - the qualitative half his own
   *  openers carry ("a 9.00% going-in cap with 2.75% annual escalations"). Omitted when absent;
   *  the computed sentence stands on its own. */
  summaryNote?: string;
  /** The "Supporting factors" paragraph. The app cannot derive which facts cut in the seller's
   *  favour, so this is supplied or the label does not print at all. */
  supportingFactors?: string;
  /** A scenario proposed from the operator's plain-language risk. The LEVERS come from the
   *  model; every FIGURE printed is computed here by the same engine the sensitivity tables use,
   *  so this page cannot disagree with them. */
  scenario?: {
    title: string;
    assumptions: Array<{ label: string; lever: string; value: number; year?: number }>;
    rationale?: string;
    notEstablished?: string[];
  };
}

/**
 * The palette - the MANCHESTER look since his ruling of 2026-09-09 ("that's going to be for the
 * investment analysis"), through the names this document already used for the August corpus.
 * Every value comes from `MANCHESTER_PDF`; the verdict colours are the house-style table.
 */
const CORPUS = {
  red: MANCHESTER_PDF.red,
  navy: MANCHESTER_PDF.navy,
  ink: MANCHESTER_PDF.ink,
  slate: MANCHESTER_PDF.muted,
  stripe: MANCHESTER_PDF.tile,
  band: MANCHESTER_PDF.tile,
  rule: MANCHESTER_PDF.rule,
  green: MANCHESTER_PDF.green,
  greenFill: MANCHESTER_PDF.greenFill,
  greenEdge: MANCHESTER_PDF.green,
  redFill: MANCHESTER_PDF.calloutFill,
  redEdge: MANCHESTER_PDF.red,
  amber: MANCHESTER_PDF.amber,
  amberFill: MANCHESTER_PDF.amberFill,
  amberEdge: MANCHESTER_PDF.amber,
  white: MANCHESTER_PDF.white,
};

const FOOTER_CONTACTS = 'Robert Mendieta, Jr., CCIM (951) 977-3251  |  Joseph Lombera (909) 406-6538';

export async function generateInvestmentAnalysisPDF(options: InvestmentAnalysisPDFOptions): Promise<ExportOutcome> {
  const {
    property,
    data,
    branding,
    photoBase64,
    aiInsights,
    keyConsiderations,
    openItems,
    summaryNote,
    supportingFactors,
    scenario,
    scoreRows,
    verdict: verdictIn,
    passed: passedIn,
    total: totalIn,
    profileName,
    structureLine,
    caveat,
    descriptor,
    sizing,
    noiOverrides,
    capitalCostsByYear,
    exitNOI: exitNOIIn,
    exitNoiSource,
    scenarioBase,
  } = options;

  // 🚨 LETTER, NOT A4. jsPDF defaults to A4 (595 x 842pt) and all six of his delivered analyses
  // are US Letter (612 x 792pt) - measured with `pdfinfo`, not assumed. An A4 client document
  // scales or crops on a US printer, and this build has already shipped that defect once on the
  // Documents page. Letter is wider and shorter, so anything tuned to A4 height must be rechecked.
  const doc = new jsPDF({ unit: 'mm', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const boldStyle: FontStyle = 'bold';
  const frame = { pageWidth, pageHeight, margin };

  // ⭐ THE FACE. Carlito - the open Calibri the Manchester specimen is set in - registered before
  // anything is drawn; every setFont below names `family`, never Helvetica. The two marks for the
  // page-1 logo row load beside it; a mark that fails to load is absent, never a broken image.
  const family = await registerAnalysisFont(doc);
  const [apexMark, kwMark] = await Promise.all([loadLogoImage('/apex-logo.png'), loadLogoImage('/kw-square.png')]);
  const tableStyles = analysisTableStyles(family);

  const BAND_H = MANCHESTER_PDF.barHeight; // the red masthead bar
  const TOP = 17; // first content line beneath the bar on an interior page
  const PAGE1_TOP = 32; // ...and on page 1, under the logo row
  const BOTTOM = 262; // last y at which a new section may start

  const n = (v: number | null | undefined) => v ?? 0;
  const firmName = branding?.company_name || 'Apex Real Estate Services';
  const headerDateOnly = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  // 🚨 THE DATE ALONE TOLD THE READER NOTHING ABOUT THE DEAL IN THEIR HAND. His band reads
  // "August 26, 2026 - $3.0M Down (1031)". Built after the financing figures resolve, below.
  let headerDate = headerDateOnly;

  // ══════════════════════════════════════════════════════════════════════
  // Derived figures - mirrors InvestmentAnalysis.tsx
  // ══════════════════════════════════════════════════════════════════════

  const strategy = (data.investment_strategy ?? 'value_add') as InvestmentStrategy;
  // Prefer what the screen scored against; only fall back when not supplied.
  const thresholds = options.thresholds ?? STRATEGY_THRESHOLDS[strategy];
  // ⛔ One map, shared with the screen. This used to carry its own copy of the ternary, and a
  // second copy is a second chance to disagree with the bar the numbers are using.
  const strategyLabel = options.thresholdLabel ?? resolveThresholdLabel(null, strategy);

  const leaseType = data.lease_type ?? 'gross';
  const expensesAreCarry = data.expenses_are_carry === true;
  /**
   * ⛔ A CARRY HAS NOBODY TO REIMBURSE IT. One resolver, so the screen and the document cannot
   * disagree about whether a recovery applies. ▶ `resolveExpenseRecoveries`.
   */
  const recoveries = resolveExpenseRecoveries({
    leaseType,
    propertyTaxes: n(data.property_taxes),
    insurance: n(data.insurance),
    repairsMaintenance: n(data.repairs_maintenance),
    nnnRecoveryRate: n(data.nnn_reimbursement_rate),
    modifiedGrossReimbursement: n(data.modified_gross_reimbursement),
    expensesAreCarry,
  });
  const nnnReimbursements = recoveries.recoveries;

  const pgi = n(data.potential_gross_income);
  const vacancyAmount = calculateVacancyAmount(pgi, n(data.vacancy_rate));
  const egi = calculateEffectiveGrossIncome(pgi, vacancyAmount, n(data.other_income) + nnnReimbursements);
  // Management fee excludes NNN pass-throughs - they are direct reimbursements,
  // not managed income.
  const egiBeforeReimbursements = pgi - vacancyAmount + n(data.other_income);
  const managementFee = calculateManagementFee(egiBeforeReimbursements, n(data.management_fee_percent));
  const totalExpenses = calculateTotalExpenses({
    propertyTaxes: n(data.property_taxes),
    insurance: n(data.insurance),
    utilities: n(data.utilities),
    managementFee,
    repairsMaintenance: n(data.repairs_maintenance),
    reservesCapex: n(data.reserves_capex),
    otherExpenses: n(data.other_expenses),
  });
  /** ⭐⭐ JJ2: in-place NOI AFTER the carry is read, through the rule the screen uses. */
  const noi = inPlaceNoi({ egi, totalExpenses, expensesAreCarry });
  /**
   * ⭐⭐⭐ THE NOI INPUTS, from the ONE builder. This document used to assemble them by hand and
   * left out `leaseUpMonths`, `propertyTaxes` and `taxGrowthRate`, so a lease-up deal exported a
   * stabilised year 1 beside a screen showing $0 - the class this build produces most.
   */
  const noiInputs = noiProjectionInputs({ data, egi, totalExpenses, noiOverrides });
  /** ⭐ KK3: the cap's caption follows the lease-up on this surface too - see `capBasisCaption`. */
  const capCaption = capBasisCaption(noiInputs.leaseUpMonths);

  const purchasePrice = n(data.purchase_price);

  const sellerCarrybackAmt = n(data.seller_carryback_amount);
  const sellerCarrybackRate = n(data.seller_carryback_rate);
  const sellerCarrybackTerm = n(data.seller_carryback_term) || 5;
  /**
   * ⭐⭐⭐ THE FINANCING FEE, resolved by the SAME function the screen calls. The PDF computing its
   * own version of anything is how it once printed DSCR 3.52x against 3.62x on the screen it was
   * exported from, and how the exit waterfall came to subtract a carryback twice.
   * ▶ `financingFee.ts`.
   */
  const financingFee = resolveFinancingFee({
    amount: data.financing_fee_amount,
    financed: data.financing_fee_financed,
    note: data.financing_fee_note,
    servicingFeeRate: data.loan_servicing_fee_rate,
    hasPrimaryNote: data.sizing_mode !== 'all_cash',
    hasSecondaryNote: sellerCarrybackAmt > 0,
  });
  /** The second note AS FINANCED - the payment and the exit payoff derive from this one amount. */
  const effectiveCarrybackAmt = sellerCarrybackAmt + financingFee.secondaryUplift;
  /** ⛔ ONE OBJECT, BOTH OUTPUTS - the document must service the note it retires. */
  const secondNote: SecondaryNote | undefined = effectiveCarrybackAmt > 0
    ? {
        amount: effectiveCarrybackAmt,
        interestRate: sellerCarrybackRate,
        amortizationYears: sellerCarrybackTerm,
        interestOnly: data.seller_carryback_interest_only === true,
      }
    : undefined;
  const sellerAnnualDS = secondNote ? secondaryNoteAnnualDebtService(secondNote) : 0;

  // The capital structure decides whether the loan or the equity is the input.
  // This is the same branch InvestmentAnalysis.tsx runs; without it an
  // equity-first deal exports a 70% LTV loan next to a "$3.0M down" headline.
  const { loanAmount, ltvPercent, downPayment } = (() => {
    if (data.sizing_mode === 'all_cash') {
      return { loanAmount: 0, ltvPercent: 0, downPayment: purchasePrice };
    }
    if (data.sizing_mode === 'equity_first' && (data.down_payment_target ?? 0) > 0) {
      const sized = sizeLoanFromEquity(purchasePrice, data.down_payment_target!, sellerCarrybackAmt);
      return {
        loanAmount: sized.loanAmount + financingFee.primaryUplift,
        ltvPercent: sized.ltvPercent,
        downPayment: data.down_payment_target!,
      };
    }
    const la = calculateLoanAmount(purchasePrice, data.ltv_percent ?? 70);
    return {
      loanAmount: la + financingFee.primaryUplift,
      ltvPercent: data.ltv_percent ?? 70,
      // ⛔ Struck on the notes NET of a financed fee, exactly as the screen does: the extra loan
      // proceeds pay the fee, not the seller, so the equity toward the purchase does not move.
      downPayment: calculateAdjustedDownPayment(purchasePrice, la, sellerCarrybackAmt),
    };
  })();

  const monthlyPmt = calculateMonthlyPayment(loanAmount, n(data.interest_rate), n(data.amortization_years));
  const bankAnnualDS = calculateAnnualDebtService(monthlyPmt);
  const annualDS = bankAnnualDS + sellerAnnualDS;
  const closingCosts = calculateClosingCosts(purchasePrice, n(data.closing_costs_percent));
  /** ⛔ `cashCost`, never `fee` - a financed fee has a note as its matching source. */
  const totalCashRequired = calculateTotalCashRequired(downPayment, closingCosts, financingFee.cashCost);
  /**
   * ⭐⭐ ONE YEAR-1 NOI FOR THE WHOLE DOCUMENT. The proforma below already prints the applied
   * contractual schedule, so striking DSCR and cash flow on the in-place figure made the PDF
   * disagree with its OWN proforma two pages later, and with the screen it was exported from -
   * 3.52x printed against a 3.62x on screen. `yearOneNoi` is the one place that decides this.
   *
   * ⛔ THE GOING-IN CAP DELIBERATELY STAYS ON THE IN-PLACE NOI. His table is headed
   * "Going-In Cap (In-Place)" and it is already right.
   */
  const yearOne = yearOneNoi(noiInputs);
  const yearOneNOI = yearOne.noi;
  // ⛔ A carry reduces cash flow and never NOI - deducted here exactly as the proforma's year 1 does.
  const beforeTaxCF = calculateBeforeTaxCashFlow(yearOneNOI, annualDS) - yearOne.carryCosts;
  const capRate = calculateCapRate(noi, purchasePrice);
  const dscr = calculateDSCR(yearOneNOI, annualDS);

  const vaTotalCost = n(data.va_capex) + n(data.va_ti_leasing) + n(data.va_carry_costs) + n(data.va_contingency);
  /**
   * ⭐⭐ THE FINANCING FEE IS PART OF PROJECT COST, whichever way it is paid. His own SBA 504
   * document states it: total project cost $5,817,000 against a $5,750,000 price, because $67,000
   * of fees rode into the debenture - and that is exactly why the CDC note is 40.7% of project
   * cost rather than 40.0% of price. Leaving it out printed the same project cost for two deals
   * that differ by the whole fee. ▶ `financingFee.ts`.
   */
  const totalProjectCost = purchasePrice + closingCosts + vaTotalCost + financingFee.fee;
  const totalCashInvested = totalCashRequired + vaTotalCost;
  const holdingPeriod = n(data.holding_period) || 5;
  const exitCapRate = n(data.exit_cap_rate) || 6;
  const sellingCostsPercent = n(data.selling_costs_percent) || 2;
  const sqft = property.building_size || 0;

  // The structure clause for the header band, from the same figures the financing table prints.
  const structureClause = data.sizing_mode === 'all_cash'
    ? 'All-Cash'
    : downPayment > 0 ? `${compactMoney(downPayment)} Down` : null;
  if (structureClause) headerDate = `${headerDateOnly} - ${structureClause}`;

  const projections = generateProjections({
    // ⭐⭐ THE NOI INPUTS the screen spreads too - lease-up, Prop 13, carry and schedule included.
    ...noiInputs,
    purchasePrice,
    loanAmount,
    interestRate: n(data.interest_rate),
    amortizationYears: n(data.amortization_years),
    holdingPeriod,
    exitCapRate,
    sellingCosts: sellingCostsPercent,
    // ⛔ The note, not the payment - the PDF's exit must retire what its cash flow services.
    secondaryLoan: secondNote,
    // The ongoing fee reaches the printed cash flow, or the document disagrees with the screen.
    servicingFeeRate: financingFee.servicingFeeRate,
      capitalCostsByYear: capitalCostsByYear ?? undefined,
  });

  const lastYear = projections.length > 0 ? projections[projections.length - 1] : null;
  /**
   * ⭐ THE FINAL HOLD YEAR, through the SHARED rule - the PDF does not get its own. This used to
   * grow `lastYear` one more year, and re-deriving a different exit here is precisely what once
   * printed DSCR 3.52x in the PDF against 3.62x on the screen. ▶ `finalHoldYearNoi`.
   */
  const projectedExitNOI = finalHoldYearNoi(projections, noi);
  const propAdditional = property.additional_data as Record<string, any> | undefined;
  const proFormaNOI = propAdditional?.pro_forma_financials?.noi as number | undefined;
  const localExitNOI = (leaseType === 'nnn' && proFormaNOI && proFormaNOI > projectedExitNOI)
    ? proFormaNOI
    : projectedExitNOI;
  // The screen's exit NOI wins outright. It is the figure the verdict card and
  // the scenario cards price their exits on, so re-deriving one here is what
  // makes a printed IRR contradict the one the client was just shown.
  const exitNOI = (exitNOIIn !== undefined && exitNOIIn !== null && isFinite(exitNOIIn))
    ? exitNOIIn
    : localExitNOI;
  // ⛔ ONE RULE, and the PDF calls it rather than restating it. See `exitNoiYearLabel`.
  const exitNOIYearLabel = exitNoiYearLabel(exitNoiSource ?? 'growth', holdingPeriod);

  const loanBalanceAtExit = lastYear?.loanBalance ?? loanAmount;
  /**
   * ⛔ THE DOCUMENT PRICES THE EXIT THE SAME WAY THE SCREEN DID. A stated exit value outranks the
   * cap rate; omit it here and the PDF prints a $0 sale on a land deal the screen priced properly,
   * which is the "two surfaces, one deal" defect that once put DSCR 3.52x in the PDF against 3.62x
   * on screen.
   */
  // ⛔ NULL/absent means "price it as a yield"; `n()` above would map an unset field to 0, which
  // `resolveExitSalePrice` also treats as absent - but being explicit keeps the intent readable.
  const statedExitValue = typeof data.exit_value === 'number' && isFinite(data.exit_value)
    ? data.exit_value
    : null;
  const saleProceeds = calculateSaleProceeds(
    exitNOI, exitCapRate, loanBalanceAtExit, sellingCostsPercent, statedExitValue);
  const irrCashFlows = buildIRRCashFlows(totalCashInvested, projections, saleProceeds.netToSeller);
  const irrValue = calculateIRR(irrCashFlows);
  const totalCashFlowsSum = projections.reduce((s, p) => s + p.cashFlow, 0);
  const equityMultiple = calculateEquityMultiple(totalCashInvested, totalCashFlowsSum, saleProceeds.netToSeller);
  const avgCashOnCash = calculateAvgCashOnCash(projections, totalCashInvested);
  const totalProfit = totalCashFlowsSum + saleProceeds.netToSeller - totalCashInvested;

  // ── Verdict / scorecard ────────────────────────────────────────────────
  // scoreRows comes from the screen. The fallback only exists so a caller that
  // has not been updated still produces a scorecard rather than a blank page.
  const fallbackRows: ScoreRow[] = [
    { key: 'cap_rate', label: capCaption.goingInCap, threshold: thresholds.cap_rate ?? 0, actual: capRate, pass: thresholds.cap_rate === null || capRate >= thresholds.cap_rate, format: 'percent', applicable: true },
    { key: 'cash_on_cash', label: 'Avg Cash-on-Cash', threshold: thresholds.cash_on_cash, actual: avgCashOnCash, pass: avgCashOnCash >= thresholds.cash_on_cash, format: 'percent', applicable: true },
    { key: 'irr', label: 'IRR', threshold: thresholds.irr, actual: irrValue, pass: irrValue >= thresholds.irr, format: 'percent', applicable: true },
    { key: 'equity_multiple', label: 'Equity Multiple', threshold: thresholds.equity_multiple, actual: equityMultiple, pass: equityMultiple >= thresholds.equity_multiple, format: 'ratio', applicable: true },
    { key: 'dscr', label: 'DSCR', threshold: thresholds.dscr, actual: dscr, pass: dscr >= thresholds.dscr, format: 'ratio', applicable: true },
  ];
  const rows: ScoreRow[] = scoreRows && scoreRows.length > 0 ? scoreRows : fallbackRows;
  const passedCount = passedIn ?? rows.filter(r => r.pass).length;
  const totalCount = totalIn ?? rows.length;
  // Fallback only fires when the caller passes no verdict, and it CALLS the same function the
  // screen does rather than restating the rule. The previous version wrote the three-state rule
  // out again here, which is defect #8 in printed form waiting to happen: a PDF claiming GO for
  // a deal the screen called something else.
  const verdict: Verdict = verdictIn ?? verdictFrom(passedCount, totalCount, openItems ?? []);
  const isGo = verdict === 'GO';
  // Both middle states print amber, not the red reserved for a dead deal. CONDITIONAL and
  // CAUTION share his palette exactly (C7860B / FBF3E2 / B9770A) - they differ in meaning.
  const isReview = isAmberVerdict(verdict);
  const profile = profileName ?? strategyLabel;

  const fmtScore = (val: number, format: 'percent' | 'ratio') =>
    format === 'ratio' ? `${val.toFixed(2)}x` : formatPercent(val);

  // ── Which optional narrative sections this deal actually earns ─────────
  // A stabilized single-tenant NNN or a ground lease has no lease-up to
  // narrate: its "pro forma" is just the contractual rent step and its
  // value-add block would be five empty rows. Gate on the DATA first - a deal
  // carrying real levers earns the section even with the archetype left unset -
  // and use the archetype only to suppress the structurally meaningless case.
  const archetype = data.archetype ?? null;
  const isStabilizedArchetype = archetype === 'stnl_nnn' || archetype === 'ground_lease';

  const vaLevers: Array<{ label: string; on: boolean; note?: string | null }> = [
    { label: 'Below-market rents', on: !!data.va_below_market_rents, note: data.va_below_market_rents_note },
    { label: 'Vacancy / lease-up', on: !!data.va_vacancy_leaseup, note: data.va_vacancy_leaseup_note },
    { label: 'Expense reduction', on: !!data.va_expense_reduction, note: data.va_expense_reduction_note },
    { label: 'Re-tenanting', on: !!data.va_re_tenanting, note: data.va_re_tenanting_note },
    { label: 'Physical improvements', on: !!data.va_physical_improvements, note: data.va_physical_improvements_note },
  ];
  const activeLevers = vaLevers.filter(l => l.on);

  const proForma = propAdditional?.pro_forma_financials as {
    noi?: number; cap_rate?: number; gross_income?: number; occupancy_rate?: number; notes?: string;
  } | undefined;

  /**
   * A pro forma figure only tells a story when it actually moves the in-place
   * number. Below 5% it is a rounding difference between two spreadsheets, and
   * printing a "Delta" column full of noise reads as a stabilization plan the
   * deal does not have.
   */
  const movesMaterially = (inPlace?: number | null, stabilized?: number | null): boolean => {
    if (!stabilized || !isFinite(stabilized) || stabilized <= 0) return false;
    if (!inPlace || !isFinite(inPlace) || inPlace <= 0) return true;
    return Math.abs(stabilized - inPlace) / inPlace >= 0.05;
  };

  const inPlaceNOI = property.noi ?? noi;
  const inPlaceIncome = property.gross_income ?? pgi;
  const occupancyLift = (proForma?.occupancy_rate ?? 0) - (property.occupancy_rate ?? 0);
  const hasStabilizationStory = !!proForma && (
    movesMaterially(inPlaceNOI, proForma.noi)
    || movesMaterially(inPlaceIncome, proForma.gross_income)
    || (proForma.occupancy_rate != null && property.occupancy_rate != null && occupancyLift >= 5)
  );
  const showProForma = hasStabilizationStory && (!isStabilizedArchetype || activeLevers.length > 0);
  const showValueAdd = !isStabilizedArchetype && (activeLevers.length > 0 || vaTotalCost > 0);

  // ══════════════════════════════════════════════════════════════════════
  // Drawing helpers
  // ══════════════════════════════════════════════════════════════════════

  const tableMargins = { left: margin, right: margin, top: TOP, bottom: 24 };

  /** Every table in this document: the family and the navy-head / zebra styles first, the call's own on top. */
  const table = (opts: Parameters<typeof autoTable>[1]) => autoTable(doc, {
    ...opts,
    styles: { ...tableStyles.styles, ...(opts.styles ?? {}) },
    headStyles: { ...tableStyles.headStyles, ...(opts.headStyles ?? {}) },
    alternateRowStyles: { ...tableStyles.alternateRowStyles, ...(opts.alternateRowStyles ?? {}) },
  });

  /** "PROPERTY OVERVIEW" - navy, bold, over a red rule, as the specimen's section titles are. */
  const section = (title: string, y: number): number => {
    if (y > BOTTOM) { doc.addPage(); y = TOP; }
    return drawAnalysisSectionTitle(doc, family, frame, title, y);
  };

  const caption = (text: string, y: number): number => {
    doc.setFontSize(7.5);
    doc.setFont(family, 'normal');
    doc.setTextColor(...CORPUS.slate);
    const lines: string[] = doc.splitTextToSize(text, contentWidth);
    doc.text(lines, margin, y);
    return y + lines.length * 3.4 + 2.5;
  };

  const note = (text: string, y: number): number => {
    doc.setFontSize(7.5);
    doc.setFont(family, 'normal');
    doc.setTextColor(70, 76, 88);
    const lines: string[] = doc.splitTextToSize(text, contentWidth);
    if (y + lines.length * 3.5 > 272) { doc.addPage(); y = TOP; }
    doc.text(lines, margin, y);
    return y + lines.length * 3.5 + 4;
  };

  // jspdf-autotable applies columnStyles to the body only, so a head label has
  // to carry its own alignment or it sits left of the column it labels.
  const headCell = (content: string, halign: 'left' | 'right' | 'center' = 'left') =>
    ({ content, styles: { halign } });

  /** A two-column money table: navy head, striped body, right-aligned amount. */
  const moneyTable = (headRow: [string, string], body: any[], y: number): number => {
    table({
      startY: y,
      head: [[headCell(headRow[0]), headCell(headRow[1], 'right')]],
      body,
      theme: 'striped',
      headStyles: {
        fillColor: CORPUS.navy,
        textColor: CORPUS.white,
        fontSize: 8.5,
        fontStyle: boldStyle,
        cellPadding: 2.8,
      },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.68 },
        1: { cellWidth: contentWidth * 0.32, halign: 'right' },
      },
      margin: tableMargins,
    });
    return (doc as any).lastAutoTable.finalY;
  };

  /** The red emphasis row the corpus uses for NOI and Total Cash Required. */
  const redRow = (label: string, value: string, spacer = 0) => {
    const cells: any[] = [
      { content: label, styles: { fillColor: CORPUS.red, textColor: CORPUS.white, fontStyle: boldStyle } },
      { content: value, styles: { fillColor: CORPUS.red, textColor: CORPUS.white, fontStyle: boldStyle, halign: 'right' } },
    ];
    for (let i = 0; i < spacer; i++) {
      cells.push({ content: '', styles: { fillColor: CORPUS.red } });
    }
    return cells;
  };

  const boldRow = (label: string, value: string) => ([
    { content: label, styles: { fontStyle: boldStyle } },
    { content: value, styles: { fontStyle: boldStyle, halign: 'right' } },
  ]);

  // ══════════════════════════════════════════════════════════════════════
  // GOAL-SEEK, HOISTED
  //
  // ⛔ ONE object, TWO consumers. Page 1's "Where the numbers point" needs the price that clears
  // the bar, and so does OPTIMIZATION eight pages later. Building it twice is the defect class
  // this build produces most often - one part of the app modelling a deal differently from
  // another - so it is computed once here and both read it.
  // ══════════════════════════════════════════════════════════════════════

  const goalSeekInputs = {
    purchasePrice,
    initialNOI: noi,
    // ⭐⭐ JJ2: the NOI inputs from the one builder - the solver used to omit the lease-up and Prop 13.
    ...noiInputs,
    loanAmount,
    interestRate: n(data.interest_rate),
    amortizationYears: n(data.amortization_years),
    holdingPeriod,
    exitCapRate,
    sellingCostsPercent,
    totalCashInvested,
    closingCostsPercent: n(data.closing_costs_percent),
    ltvPercent,
    buildingSqft: sqft,
    expenseRatio: (totalExpenses / (egi || 1)) * 100,
    capitalCostsByYear: capitalCostsByYear ?? undefined,
    exitNOIOverride: exitNOIIn,
    sizingMode: data.sizing_mode,
    downPaymentTarget: data.down_payment_target,
    sellerAnnualDS,
  };

  // Solve against the IRR the scorecard just scored, so a "GO" and a zero gap always agree.
  // Falls back to the strategy threshold only when the caller supplied rows that do not gate IRR.
  const targetIRR = rows.find(r => r.key === 'irr')?.threshold ?? thresholds.irr;
  const maxPurchasePrice = purchasePrice > 0 && egi > 0 ? solveMaxPurchasePrice(targetIRR, goalSeekInputs) : 0;

  // ══════════════════════════════════════════════════════════════════════
  // PAGE 1 - masthead, verdict, hero strip
  // ══════════════════════════════════════════════════════════════════════

  let yPos = PAGE1_TOP;

  /**
   * ⭐ THE MASTHEAD, against the reference he asked for. His Cowork analyses lead with a BOLD,
   * brand-coloured title rather than a light 30pt grey one; the rule under it already carries the
   * red. Smaller and heavier reads as a document rather than a slide.
   */
  doc.setTextColor(...CORPUS.red);
  doc.setFontSize(26);
  doc.setFont(family, boldStyle);
  doc.text('INVESTMENT ANALYSIS', margin, yPos);
  yPos += 4.5;
  doc.setDrawColor(...CORPUS.red);
  doc.setLineWidth(1.4);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 9;

  // Address and deal name. The corpus prints "13720 Bear Valley Road (AutoZone
  // & Dollar Tree, Victorville)" - the parenthetical is the deal's trading
  // name, so it is only rendered when the record actually carries one.
  const addressLine = property.address || 'Address Not Available';
  const dealNameRaw = (propAdditional?.deal_name ?? propAdditional?.property_name) as string | undefined;
  const headline = dealNameRaw ? `${addressLine} (${dealNameRaw})` : addressLine;
  doc.setFontSize(14);
  doc.setFont(family, boldStyle);
  doc.setTextColor(...CORPUS.navy);
  doc.text(headline, margin, yPos);
  yPos += 5.5;

  const cityLine = [
    [property.city, property.state].filter(Boolean).join(', '),
    property.zip_code,
  ].filter(Boolean).join(' ');
  doc.setFontSize(9);
  doc.setTextColor(...CORPUS.slate);
  doc.text(cityLine, margin, yPos);
  yPos += 5;

  // ⭐ Everything here comes off the record. His subtitles carry the guarantor, the term and the
  // escalations; the app prints whichever of those the analysis actually holds and stays silent
  // on the rest rather than padding the line.
  const subtitleLeaseLabel = leaseType === 'nnn' ? 'NNN'
    : leaseType === 'modified_gross' ? 'MODIFIED GROSS'
    : leaseType === 'absolute_nnn' ? 'ABSOLUTE NNN' : null;
  const descriptorHead = (descriptor ?? [
    property.property_type?.replace(/_/g, ' ').toUpperCase(),
    subtitleLeaseLabel,
  ].filter(Boolean).join(' ')).toUpperCase();
  // ⚠️ DEDUPE AGAINST THE HEAD. The archetype descriptor already says "19,300 SF | 100% Leased",
  // so appending them blind printed each one twice on the specimen deal.
  const alreadySaid = (clause: string) =>
    descriptorHead.includes(clause.replace(/-BUILT$/, '').replace(/% LEASED$/, '%').trim());
  const descriptorTail = [
    sqft ? `${formatNumber(sqft)} SF` : null,
    property.year_built ? `${property.year_built}-BUILT` : null,
    property.occupancy_rate ? `${property.occupancy_rate}% LEASED` : null,
    holdingPeriod ? `${holdingPeriod}-YR HOLD` : null,
    n(data.income_growth_rate) > 0 ? `${n(data.income_growth_rate).toFixed(2)}% ANNUAL INCREASES` : null,
  ].filter((c): c is string => !!c && !alreadySaid(c));
  const descriptorLine = [descriptorHead, ...descriptorTail].filter(Boolean).join(' | ');
  doc.setFontSize(9);
  doc.setTextColor(...CORPUS.ink);
  const descLines: string[] = doc.splitTextToSize(descriptorLine, contentWidth);
  doc.text(descLines, margin, yPos);
  yPos += descLines.length * 4.4 + 5;

  // ── Verdict box + the summary callout ──────────────────────────────────
  //
  // ⭐⭐ THE CALLOUT IS THE DOCUMENT'S FIRST PAGE, and it was missing entirely - the app printed
  // two thin lines and left a third of page 1 blank white. Every one of his six analyses leads
  // with this block: a finding, the deal on one line, then Items to verify / Supporting factors /
  // Where the numbers point.
  //
  // ⛔ EVERY SENTENCE HERE IS COMPUTED FROM THE RECORD, not written by a model. The finding is
  // the scorecard restated, the deal line is arithmetic, "Items to verify" is the operator's own
  // `open_items` list, and "Where the numbers point" is the goal-seek that OPTIMIZATION prints
  // later. Prose the app cannot derive is accepted from the caller and simply omitted when
  // absent - facts that refuse, rather than a model filling the space.
  const vx = margin + 55;
  const vWidth = contentWidth - 55;

  /** A labelled paragraph: navy bold lead-in, body in ink, wrapping under the label. */
  const labelled = (label: string, body: string, y: number): number => {
    doc.setFontSize(7.6);
    doc.setFont(family, boldStyle);
    doc.setTextColor(...CORPUS.navy);
    const lw = doc.getTextWidth(label + ' ');
    doc.text(label, vx, y);
    doc.setFont(family, 'normal');
    doc.setTextColor(...CORPUS.ink);
    const first: string[] = doc.splitTextToSize(body, vWidth - lw);
    doc.text(first[0] ?? '', vx + lw, y);
    let yy = y;
    const restText = (first.slice(1).join(' ') || '').trim();
    if (restText) {
      const rest: string[] = doc.splitTextToSize(restText, vWidth);
      doc.text(rest, vx, yy + 3.5);
      yy += rest.length * 3.5;
    }
    return yy + 5.2;
  };

  const plain = (body: string, y: number, size = 7.6): number => {
    doc.setFontSize(size);
    doc.setFont(family, 'normal');
    doc.setTextColor(...CORPUS.ink);
    const ls: string[] = doc.splitTextToSize(body, vWidth);
    doc.text(ls, vx, y);
    return y + ls.length * 3.5 + 2.6;
  };

  // 1. THE FINDING - the scorecard in a sentence, plus why this status and not the one above it.
  /**
   * 🚨 THIS SENTENCE USED TO ARGUE WITH ITSELF. The counts come from the caller (`passed`/`total`
   * win over `rows` at their declaration) while the miss list was always computed from `rows`, so
   * a specimen render printed "Five of five applicable metrics clear the stated criteria. Going-In
   * Cap and Avg Cash-on-Cash and IRR and Equity Multiple are the misses." ▶ `scoreFinding`, which
   * names misses only when the rows corroborate the counts, and joins them "A, B and C".
   */
  const missRows = rows.filter(r => r.applicable && !r.pass);
  const wordFor = (k: number) => ['no', 'one', 'two', 'three', 'four', 'five'][k] ?? String(k);
  const findingHead = scoreFinding({
    rows,
    passedCount,
    totalCount,
    describeMiss: (r) => {
      const match = missRows.find(m => m.label === r.label);
      return match ? fmtScore(match.actual, match.format) : null;
    },
  });
  const listedItems = (openItems ?? []).filter(i => i && i.trim() !== '');
  const statusWhy =
    verdict === 'CONDITIONAL'
      ? ` The status is CONDITIONAL rather than GO because ${listedItems.length === 1 ? 'one open item sits' : `${wordFor(listedItems.length)} open items sit`} outside the arithmetic and could change the structure rather than the numbers.`
      : verdict === 'GO' ? ' No material items are outstanding.'
      : '';

  let vy = yPos + 5;
  vy = plain(findingHead + statusWhy + (summaryNote ? ' ' + summaryNote : ''), vy, 8);

  // 2. THE DEAL, on one line. Pure arithmetic off the same figures the rest of the document uses.
  const dealBits: string[] = [
    `${formatCurrency(purchasePrice)} ask @ ${formatPercent(capRate)} ${capCaption.band}${sqft > 0 ? ` / $${(purchasePrice / sqft).toFixed(2)}/SF` : ''}`,
    data.sizing_mode === 'all_cash'
      ? `all-cash, ${formatCurrency(totalCashInvested)} in`
      : `${formatCurrency(downPayment)} down + ${formatCurrency(loanAmount)} loan (${ltvPercent.toFixed(1)}% LTV) / ${formatCurrency(totalCashInvested)} in`,
    `${formatPercent(avgCashOnCash)} avg cash-on-cash, ${formatPercent(irrValue)} IRR base / ${formatRatio(equityMultiple)} EM (${formatPercent(exitCapRate)} exit)`,
  ];
  if (data.sizing_mode !== 'all_cash' && dscr > 0) dealBits.push(`DSCR ${formatRatio(dscr)}`);
  // ⚠️ The caller also passes a one-clause `structureLine`. The computed line is strictly richer
  // and built from the same figures, so it wins; the caller's is appended only if it says
  // something the computed line does not.
  const dealLine = dealBits.join(' - ') + '.';
  vy = plain(dealLine, vy);
  if (structureLine && !dealLine.includes(formatCurrency(downPayment))) {
    vy = plain(structureLine, vy, 7.2);
  }

  // 3. The three labels, in his order. Each omitted when it has nothing to say.
  if (listedItems.length > 0) vy = labelled('Items to verify:', listedItems.join('; ') + '.', vy);
  if (supportingFactors) vy = labelled('Supporting factors:', supportingFactors, vy);

  const wnp: string[] = [`At ask the analysis returns ${formatPercent(irrValue)} IRR and ${formatRatio(equityMultiple)} equity multiple.`];
  if (maxPurchasePrice > 0 && Math.abs(maxPurchasePrice - purchasePrice) > 1000) {
    const solvedCap = maxPurchasePrice > 0 ? (noi / maxPurchasePrice) * 100 : 0;
    wnp.push(
      maxPurchasePrice < purchasePrice
        ? `At ${formatCurrency(maxPurchasePrice)} (${formatPercent(solvedCap)} cap) it reaches the ${formatPercent(targetIRR)} bar, ${formatCurrency(purchasePrice - maxPurchasePrice)} below the ask.`
        : `The ${formatPercent(targetIRR)} bar is already cleared; it holds up to ${formatCurrency(maxPurchasePrice)} (${formatPercent(solvedCap)} cap).`
    );
  }
  vy = labelled('Where the numbers point:', wnp.join(' '), vy);

  // The box spans the callout, as his does, and never shrinks below its own text.
  const boxW = 48;
  const boxH = Math.max(21, vy - yPos - 3);
  doc.setFillColor(...(isGo ? CORPUS.greenFill : isReview ? CORPUS.amberFill : CORPUS.redFill));
  doc.setDrawColor(...(isGo ? CORPUS.greenEdge : isReview ? CORPUS.amberEdge : CORPUS.redEdge));
  doc.setLineWidth(0.55);
  doc.rect(margin, yPos, boxW, boxH, 'FD');
  doc.setFontSize(verdict.length > 8 ? 15 : 20);
  doc.setFont(family, boldStyle);
  doc.setTextColor(...(isGo ? CORPUS.green : isReview ? CORPUS.amber : CORPUS.red));
  doc.text(verdict, margin + boxW / 2, yPos + boxH / 2 + 2, { align: 'center' });
  doc.setFontSize(6.4);
  doc.setTextColor(...CORPUS.slate);
  doc.text(`${profile} | ${passedCount}/${totalCount}`, margin + boxW / 2, yPos + boxH - 4, { align: 'center' });
  if (caveat) {
    doc.setFontSize(7);
    doc.setTextColor(...CORPUS.red);
    doc.text(doc.splitTextToSize(caveat, vWidth)[0], vx, vy);
    vy += 4;
  }

  yPos = Math.max(yPos + boxH, vy) + 7;

  // ── Five-metric hero strip ─────────────────────────────────────────────
  const heroH = 17;
  // ⭐ THE FIVE METRICS ARE HIS, MEASURED OFF THE DELIVERED PDFs, not chosen here. All six of
  // 2026-08-26 print: ASKING PRICE · PRICE / SF · IN-PLACE NOI · GOING-IN CAP · 5-YR IRR.
  // 🚨 `TOTAL PROJECT COST` used to hold the second slot and $/SF appeared NOWHERE on page 1 -
  // the number a broker quotes first on an industrial or retail deal.
  // ⚠️ The IRR accent tracks whether IRR ITSELF cleared its bar, not the overall verdict. His
  // STIIIZY prints a green 10.35% on a CONDITIONAL deal; colouring by verdict would print that
  // amber and tell the reader the metric missed when it passed.
  const irrRow = rows.find(r => r.key === 'irr');
  const irrAccent: [number, number, number] | undefined =
    !irrRow || !irrRow.applicable ? undefined : irrRow.pass ? CORPUS.green : CORPUS.red;
  const heroCells: Array<{ label: string; value: string; accent?: [number, number, number] }> = [
    { label: 'ASKING PRICE', value: formatCurrency(purchasePrice) },
    { label: 'PRICE / SF', value: sqft > 0 ? `$${(purchasePrice / sqft).toFixed(2)}/SF` : 'n/a' },
    { label: capCaption.noi.toUpperCase(), value: formatCurrency(noi) },
    { label: capCaption.goingInCap.toUpperCase(), value: formatPercent(capRate) },
    { label: `${holdingPeriod}-YR IRR`, value: formatPercent(irrValue), accent: irrAccent },
  ];
  // Five separate tiles with a gap between them, label over figure, as the specimen's strip.
  const heroGap = 2;
  const heroW = (contentWidth - heroGap * (heroCells.length - 1)) / heroCells.length;
  heroCells.forEach((cell, i) => {
    const x0 = margin + i * (heroW + heroGap);
    doc.setFillColor(...CORPUS.band);
    doc.rect(x0, yPos, heroW, heroH, 'F');
    const cx = x0 + heroW / 2;
    doc.setFontSize(6.4);
    doc.setFont(family, boldStyle);
    doc.setTextColor(...CORPUS.slate);
    doc.text(cell.label, cx, yPos + 5.6, { align: 'center' });
    doc.setFontSize(12.5);
    doc.setTextColor(...(cell.accent ?? CORPUS.ink));
    doc.text(cell.value, cx, yPos + 13.2, { align: 'center' });
  });
  yPos += heroH + 10;

  // ── Building photo ─────────────────────────────────────────────────────
  // Placed under the hero strip rather than above the verdict so page 1 still
  // leads with the decision and the five headline metrics - the photo is
  // context for them, not the headline itself.
  if (photoBase64) {
    try {
      const photoH = 70;
      if (yPos + photoH > 262) { doc.addPage(); yPos = TOP; }
      doc.addImage(photoBase64, 'AUTO', margin, yPos, contentWidth, photoH);
      doc.setDrawColor(...CORPUS.rule);
      doc.setLineWidth(0.4);
      doc.rect(margin, yPos, contentWidth, photoH);
      // His italic caption carries facts, not decoration: size, acreage, what the site is.
      // Built from the record; clauses the record cannot fill are simply left out.
      const capBits = [property.address, [property.city, property.state].filter(Boolean).join(', ')]
        .filter(Boolean).join(', ');
      const capFacts = [
        [sqft ? `a ${formatNumber(sqft)} SF building` : null,
         property.lot_size ? `on +/-${(property.lot_size / 43560).toFixed(2)} AC` : null].filter(Boolean).join(' '),
        property.year_built ? `built ${property.year_built}` : null,
        property.occupancy_rate ? `${property.occupancy_rate}% leased` : null,
      ].filter(Boolean).join(', ');
      if (capBits || capFacts) {
        doc.setFontSize(6.8);
        doc.setFont(family, 'italic');
        doc.setTextColor(...CORPUS.slate);
        const capLines: string[] = doc.splitTextToSize(
          [capBits, capFacts].filter(Boolean).join(' - ') + '.', contentWidth - 10);
        doc.text(capLines, pageWidth / 2, yPos + photoH + 4, { align: 'center' });
        doc.setFont(family, 'normal');
        yPos += capLines.length * 3.2;
      }
      yPos += photoH + 9;
      // A section header plus a usable run of table rows needs roughly 100mm.
      // With less than that left, the page ends on the photo rather than
      // orphaning three rows and the NOI line across the fold.
      if (yPos > 175) { doc.addPage(); yPos = TOP; }
    } catch {
      // The photo is decorative. A source the PDF engine cannot decode must
      // not cost the client the rest of the analysis.
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // PROPERTY OVERVIEW
  //
  // ⭐ HIS SECOND PAGE, AND THE APP HAD NO EQUIVALENT. It went straight from the hero photo into
  // INCOME & EXPENSE SUMMARY, so a reader met the rent roll before they were told what the
  // building is. Every row is drawn from the record.
  //
  // ⛔ FACTS THAT REFUSE. His table also carries Setting, Access, Nearby Demand, Tax Designation
  // and Listing Broker. The analyzer record holds none of those, so those rows are NOT PRINTED
  // rather than filled with a guess - a blank he can type beats a sentence he has to catch.
  // ══════════════════════════════════════════════════════════════════════

  if (yPos > 200) { doc.addPage(); yPos = TOP; }
  yPos = section('PROPERTY OVERVIEW', yPos);

  const overviewBody: any[] = [];
  const addOverview = (label: string, value: string | null | undefined) => {
    if (value && value !== 'N/A' && value !== '0') overviewBody.push([label, value]);
  };
  addOverview('Address', [
    property.address,
    [[property.city, property.state].filter(Boolean).join(', '), property.zip_code].filter(Boolean).join(' '),
  ].filter(Boolean).join(', '));
  addOverview('Asking Price', sqft > 0
    ? `${formatCurrency(purchasePrice)} ($${(purchasePrice / sqft).toFixed(2)}/SF)`
    : formatCurrency(purchasePrice));
  addOverview('Capital Structure', data.sizing_mode === 'all_cash'
    ? `All-cash - no loan; ${formatCurrency(totalCashInvested)} deployed`
    : `${formatCurrency(downPayment)} down + ${formatCurrency(loanAmount)} loan (${ltvPercent.toFixed(1)}% LTV)`);
  addOverview(`NOI - ${capCaption.basis}`, formatCurrency(noi));
  addOverview(`Cap Rate - ${capCaption.basis}`, formatPercent(capRate));
  addOverview('Net Rentable Area', sqft > 0
    ? `+/-${formatNumber(sqft)} SF${property.lot_size ? ` on +/-${(property.lot_size / 43560).toFixed(2)} AC` : ''}`
    : null);
  addOverview('Year Built', property.year_built ? String(property.year_built) : null);
  addOverview('Occupancy', property.occupancy_rate ? `${property.occupancy_rate}%` : null);
  addOverview('Lease Structure', subtitleLeaseLabel ?? (leaseType ? leaseType.replace(/_/g, ' ').toUpperCase() : null));
  addOverview('Hold / Exit Assumption', holdingPeriod ? `${holdingPeriod}-year hold at a ${formatPercent(exitCapRate)} exit cap` : null);
  const highlightRows = (propAdditional?.property_highlights as string[] | undefined) ?? [];
  if (highlightRows.length > 0) addOverview('Property Highlights', highlightRows.slice(0, 3).join('; '));
  const marketRows = (propAdditional?.market_highlights as string[] | undefined) ?? [];
  if (marketRows.length > 0) addOverview('Market Context', marketRows.slice(0, 3).join('; '));
  addOverview('Document Status', listedItems.length > 0
    ? `${listedItems.length} item${listedItems.length === 1 ? '' : 's'} outstanding - see Items to verify on page 1`
    : 'No material items outstanding');

  yPos = moneyTable(['Item', 'Detail'], overviewBody, yPos) + 10;

  // ══════════════════════════════════════════════════════════════════════
  // INCOME & EXPENSE SUMMARY
  // ══════════════════════════════════════════════════════════════════════

  yPos = section('INCOME & EXPENSE SUMMARY', yPos);

  const leaseLabel = leaseType === 'nnn' ? ' (NNN)' : leaseType === 'modified_gross' ? ' (MODIFIED GROSS)' : '';
  const pdfTenants = propAdditional?.tenants as Array<{
    tenant_name?: string; leased_sf?: number; monthly_base_rent?: number; annual_rent?: number;
    lease_type?: string; lease_end?: string; tenant_credit?: string; notes?: string;
  }> | undefined;
  const isNNN = leaseType === 'nnn';
  const incIncludesReimb = propAdditional?.income_includes_reimbursements === true
    || propAdditional?.income_includes_reimbursements === 'true';
  const hasBaseRent = propAdditional?.base_rent && Number(propAdditional.base_rent) > 0;
  const hasNNNRecoveries = propAdditional?.expense_reimbursements && Number(propAdditional.expense_reimbursements) > 0;

  const incomeBody: any[] = [];

  if (incIncludesReimb && hasBaseRent) {
    // The stated revenue line already folds in recoveries - show its parts.
    incomeBody.push(['Scheduled Base Rent', formatCurrency(Number(propAdditional!.base_rent))]);
    if (hasNNNRecoveries) {
      incomeBody.push(['Expense Recoveries (NNN)', formatCurrency(Number(propAdditional!.expense_reimbursements))]);
    }
    if (propAdditional?.percentage_rent && Number(propAdditional.percentage_rent) > 0) {
      incomeBody.push(['Percentage Rent', formatCurrency(Number(propAdditional.percentage_rent))]);
    }
    if (propAdditional?.other_income_detail && Number(propAdditional.other_income_detail) > 0) {
      incomeBody.push(['Other Income', formatCurrency(Number(propAdditional.other_income_detail))]);
    }
    incomeBody.push(boldRow('Effective Gross Revenue to Landlord', formatCurrency(pgi)));
    incomeBody.push(['Total Operating Expenses', formatCurrency(totalExpenses)]);
  } else if (isNNN && pdfTenants && pdfTenants.length > 0) {
    const occupied = pdfTenants.filter(t => t.tenant_name && !t.tenant_name.toLowerCase().includes('vacant'));
    for (const t of occupied) {
      const annualRent = t.annual_rent ?? ((t.monthly_base_rent ?? 0) * 12);
      const parts = [
        t.leased_sf ? `${formatNumber(t.leased_sf)} SF` : null,
        (t.lease_type ?? 'NNN').toUpperCase(),
        t.monthly_base_rent ? `${formatCurrency(t.monthly_base_rent)}/mo` : null,
      ].filter(Boolean).join(', ');
      incomeBody.push([parts ? `${t.tenant_name} (${parts})` : `${t.tenant_name}`, formatCurrency(annualRent)]);
    }
    incomeBody.push(['Total Scheduled Base Rent (NNN)', formatCurrency(pgi)]);
    incomeBody.push(['Tenant Reimbursements (taxes/ins/CAM)', 'Pass-through']);
    incomeBody.push(['Effective Gross Revenue to Landlord', formatCurrency(egi)]);
    incomeBody.push(['Total Operating Expenses', formatCurrency(totalExpenses)]);
  } else {
    incomeBody.push(['Potential Gross Income (PGI)', formatCurrency(pgi)]);
    incomeBody.push([`Less: Vacancy (${n(data.vacancy_rate)}%)`, `(${formatCurrency(vacancyAmount)})`]);
    incomeBody.push(['Plus: Other Income', formatCurrency(n(data.other_income))]);
    if (nnnReimbursements > 0) {
      incomeBody.push(['Plus: Expense Reimbursements', formatCurrency(nnnReimbursements)]);
    }
    incomeBody.push(boldRow('Effective Gross Income (EGI)', formatCurrency(egi)));
    incomeBody.push(['Total Operating Expenses', formatCurrency(totalExpenses)]);
  }

  incomeBody.push(redRow('Net Operating Income (NOI)', formatCurrency(noi)));
  yPos = moneyTable([`INCOME${leaseLabel}`, 'Annual'], incomeBody, yPos) + 10;

  // ══════════════════════════════════════════════════════════════════════
  // OPERATING EXPENSES (ANNUAL)
  // ══════════════════════════════════════════════════════════════════════

  yPos = section('OPERATING EXPENSES (ANNUAL)', yPos);

  const perSF = (amount: number) => sqft > 0 ? `$${(amount / sqft).toFixed(2)}/SF` : 'N/A';
  const expenseLines: Array<[string, number]> = [
    ['Property Taxes', n(data.property_taxes)],
    ['Insurance', n(data.insurance)],
    ['Utilities', n(data.utilities)],
    [`Management Fee (${formatFeeRate(n(data.management_fee_percent))})`, managementFee],
    ['Repairs & Maintenance', n(data.repairs_maintenance)],
    ['Reserves / CapEx', n(data.reserves_capex)],
    ['Other Expenses', n(data.other_expenses)],
  ];

  const expenseBody: any[] = [];
  if (isNNN && totalExpenses === 0) {
    // A pure NNN deal has no landlord expense line to print; the corpus states
    // the structure rather than showing seven $0 rows with no explanation.
    expenseBody.push(['NNN Lease Structure - Tenants Pay All Operating Expenses', formatCurrency(0), perSF(0)]);
  }
  for (const [label, amount] of expenseLines) {
    expenseBody.push([
      isNNN && totalExpenses === 0 ? `${label} - Tenants Reimburse Landlord` : label,
      formatCurrency(amount),
      perSF(amount),
    ]);
  }
  expenseBody.push([
    { content: 'Total Operating Expenses', styles: { fontStyle: boldStyle } },
    { content: formatCurrency(totalExpenses), styles: { fontStyle: boldStyle, halign: 'right' } },
    { content: perSF(totalExpenses), styles: { fontStyle: boldStyle, halign: 'right' } },
  ]);
  expenseBody.push([
    { content: 'Net Operating Income (NOI)', styles: { fillColor: CORPUS.red, textColor: CORPUS.white, fontStyle: boldStyle } },
    { content: formatCurrency(noi), styles: { fillColor: CORPUS.red, textColor: CORPUS.white, fontStyle: boldStyle, halign: 'right' } },
    { content: '', styles: { fillColor: CORPUS.red } },
  ]);

  table({
    startY: yPos,
    head: [[headCell('Operating Expense'), headCell('Annual', 'right'), headCell('Per SF', 'right')]],
    body: expenseBody,
    theme: 'striped',
    headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
    alternateRowStyles: { fillColor: CORPUS.stripe },
    styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.56 },
      1: { cellWidth: contentWidth * 0.22, halign: 'right' },
      2: { cellWidth: contentWidth * 0.22, halign: 'right' },
    },
    margin: tableMargins,
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;

  // ══════════════════════════════════════════════════════════════════════
  // FINANCING & CASH REQUIRED
  // ══════════════════════════════════════════════════════════════════════

  yPos = section('FINANCING & CASH REQUIRED', yPos);

  const financingBody: any[] = [['Purchase Price', formatCurrency(purchasePrice)]];
  if (data.sizing_mode === 'all_cash') {
    financingBody.push(['Bank Loan', 'All-cash - no financing']);
  } else {
    const ltvLabel = data.sizing_mode === 'equity_first'
      ? `Bank Loan (${ltvPercent.toFixed(1)}% LTV - sized to keep ${formatCurrency(downPayment)} cash equity)`
      : `Bank Loan (${ltvPercent.toFixed(1)}% LTV)`;
    financingBody.push([ltvLabel, formatCurrency(loanAmount)]);
  }
  if (effectiveCarrybackAmt > 0) {
    const carrybackLabel = financingFee.secondaryUplift > 0
      ? `Second Note (incl. ${formatCurrency(financingFee.secondaryUplift)} of financed fees)`
      : 'Seller Carryback';
    financingBody.push([carrybackLabel, formatCurrency(effectiveCarrybackAmt)]);
  }
  financingBody.push(['Down Payment', formatCurrency(downPayment)]);
  financingBody.push([`Closing Costs (${n(data.closing_costs_percent)}%)`, formatCurrency(closingCosts)]);
  /**
   * ⭐⭐ THE FEE PRINTS EITHER WAY, and the LABEL says which mechanic applied. A deduction the
   * reader cannot see is a number that appears from nowhere - and a financed fee that showed no
   * line at all would leave the note silently larger than the purchase money beside it.
   */
  if (financingFee.fee > 0) {
    financingBody.push([
      financingFee.cashCost > 0
        ? 'Financing Fee (paid at closing)'
        : 'Financing Fee (financed into the loan - no cash)',
      formatCurrency(financingFee.fee),
    ]);
  }
  if (vaTotalCost > 0) {
    financingBody.push(['Value-Add / CapEx Budget', formatCurrency(vaTotalCost)]);
  }
  // Total project cost moved here when the hero strip took his five metrics instead. It is real
  // data and belongs in the sources-and-uses, not deleted along with the slot it used to occupy.
  financingBody.push([financingFee.fee > 0
      ? 'Total Project Cost (price + closing + capex + financing fee)'
      : 'Total Project Cost (price + closing + capex)', formatCurrency(totalProjectCost)]);
  financingBody.push(redRow('Total Cash Required', formatCurrency(totalCashInvested)));
  yPos = moneyTable(['Investment Summary', 'Amount'], financingBody, yPos) + 10;

  // ══════════════════════════════════════════════════════════════════════
  // PRIMARY LOAN TERMS
  // ══════════════════════════════════════════════════════════════════════

  yPos = section('PRIMARY LOAN TERMS', yPos);

  const loanBody: any[] = [
    ['Interest Rate', `${n(data.interest_rate).toFixed(2)}%`],
    ['Amortization Period', `${n(data.amortization_years)} years`],
    ['Monthly Payment', formatCurrency(monthlyPmt)],
    ['Annual Debt Service', formatCurrency(bankAnnualDS)],
  ];
  if (sellerCarrybackAmt > 0) {
    loanBody.push(
      ['Seller Carryback Rate', `${sellerCarrybackRate.toFixed(2)}%`],
      ['Seller Carryback Term', `${sellerCarrybackTerm} years`],
      ['Seller Carryback Annual DS', formatCurrency(sellerAnnualDS)],
      boldRow('Total Annual Debt Service', formatCurrency(annualDS)),
    );
  }
  loanBody.push(
    ['DSCR (Year 1)', `${dscr.toFixed(2)}x`],
    ['Cash-on-Cash (Year 1)', formatPercent(calculateCashOnCash(beforeTaxCF, totalCashInvested))],
    ['Year 1 Cash Flow', formatCurrency(beforeTaxCF)],
  );
  yPos = moneyTable(['Metric', 'Value'], loanBody, yPos) + 6;

  if (sizing && data.sizing_mode !== 'all_cash') {
    const constraintLabel = sizing.constraint === 'ltv'
      ? 'LTV'
      : sizing.constraint === 'dscr'
        ? 'DSCR covenant'
        : sizing.constraint === 'debt_yield'
          ? 'debt yield'
          : 'no binding test';
    yPos = note(
      `Lender sizing: the binding constraint is ${constraintLabel}, capping the loan at `
      + `${formatCurrency(sizing.maxLoan)}. This deal requests ${formatCurrency(loanAmount)}, `
      + `${loanAmount <= sizing.maxLoan ? 'inside' : 'above'} that ceiling. `
      + `Tests - LTV ${isFinite(sizing.byLtv) ? formatCurrency(sizing.byLtv) : 'not applied'}; `
      + `DSCR ${isFinite(sizing.byDscr) ? formatCurrency(sizing.byDscr) : 'not applied'}; `
      + `debt yield ${isFinite(sizing.byDebtYield) ? formatCurrency(sizing.byDebtYield) : 'not applied'}.`,
      yPos
    );
  }
  yPos += 4;

  // ══════════════════════════════════════════════════════════════════════
  // RENT ROLL & TENANT SUMMARY
  // ══════════════════════════════════════════════════════════════════════

  if (pdfTenants && pdfTenants.length > 0) {
    yPos = section('RENT ROLL & TENANT SUMMARY', yPos);

    const rentRollBody = pdfTenants.map(t => {
      const isVacant = (t.tenant_name ?? '').toLowerCase().includes('vacant');
      return [
        t.tenant_name || 'Unknown',
        t.leased_sf ? formatNumber(t.leased_sf) : '-',
        isVacant ? '-' : (t.monthly_base_rent ? formatCurrency(t.monthly_base_rent) : '-'),
        t.lease_end || '-',
        t.notes || (isVacant ? 'Lease-up opportunity' : (t.tenant_credit ? `${t.tenant_credit} credit` : '-')),
      ];
    });

    table({
      startY: yPos,
      head: [[
        headCell('Tenant'), headCell('SF', 'right'), headCell('Monthly Rent', 'right'),
        headCell('Lease End', 'center'), headCell('Notes'),
      ]],
      body: rentRollBody,
      theme: 'striped',
      headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8, fontStyle: boldStyle, cellPadding: 2.6 },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 7.8, cellPadding: 2.6, textColor: CORPUS.ink, lineWidth: 0, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.30 },
        1: { cellWidth: contentWidth * 0.10, halign: 'right' },
        2: { cellWidth: contentWidth * 0.14, halign: 'right' },
        3: { cellWidth: contentWidth * 0.14, halign: 'center' },
        4: { cellWidth: contentWidth * 0.32 },
      },
      margin: tableMargins,
    });
    yPos = (doc as any).lastAutoTable.finalY + 5;

    const leaseProfile = (propAdditional?.lease_profile ?? propAdditional?.tenant_summary) as string | undefined;
    if (leaseProfile) {
      yPos = note(`Lease Profile: ${leaseProfile}`, yPos);
    } else {
      const national = pdfTenants.filter(t => t.tenant_credit?.toLowerCase() === 'national').length;
      const regional = pdfTenants.filter(t => t.tenant_credit?.toLowerCase() === 'regional').length;
      const local = pdfTenants.filter(t => t.tenant_credit?.toLowerCase() === 'local').length;
      const vacant = pdfTenants.filter(t => t.tenant_name?.toLowerCase().includes('vacant')).length;
      const leasedSF = pdfTenants
        .filter(t => !t.tenant_name?.toLowerCase().includes('vacant'))
        .reduce((s, t) => s + (t.leased_sf ?? 0), 0);
      const mix = [
        national > 0 ? `National: ${national}` : null,
        regional > 0 ? `Regional: ${regional}` : null,
        local > 0 ? `Local: ${local}` : null,
        vacant > 0 ? `Vacant suites: ${vacant}` : null,
      ].filter(Boolean).join('  |  ');
      yPos = note(
        `Tenant Mix: ${mix || `${pdfTenants.length} tenants`}. `
        + `Leased area ${formatNumber(leasedSF)} SF of ${sqft > 0 ? formatNumber(sqft) : 'n/a'} SF building.`,
        yPos
      );
    }
    yPos += 2;
  }

  // ══════════════════════════════════════════════════════════════════════
  // MARKET CONTEXT
  //
  // Traffic, frontage, anchors and radius demographics as extracted from the
  // OM. Printed whenever the record carries any of it; a property with none
  // gets no heading rather than an empty table.
  // ══════════════════════════════════════════════════════════════════════

  const marketCtx = propAdditional?.market_context as Record<string, any> | undefined;
  if (marketCtx) {
    const marketRows: Array<[string, string]> = [];
    const num = (v: any): string => Number(v).toLocaleString('en-US');

    const trafficCount = marketCtx.traffic_counts ?? marketCtx.traffic_count;
    if (trafficCount) {
      marketRows.push(['Traffic Count', typeof trafficCount === 'string' ? trafficCount : `${trafficCount}`]);
    }
    if (marketCtx.daily_traffic) {
      marketRows.push(['Daily Traffic', `${num(marketCtx.daily_traffic)} vehicles`]);
    }
    if (marketCtx.frontage) {
      marketRows.push(['Frontage', String(marketCtx.frontage)]);
    }
    if (marketCtx.highway_access) {
      marketRows.push(['Highway Access', String(marketCtx.highway_access)]);
    }
    const anchors = marketCtx.nearby_anchors;
    if (Array.isArray(anchors) ? anchors.length > 0 : !!anchors) {
      marketRows.push(['Nearby Anchors', Array.isArray(anchors) ? anchors.join(', ') : String(anchors)]);
    }
    const demo = marketCtx.demographics as Record<string, any> | undefined;
    if (demo) {
      if (demo.population_1mi) marketRows.push(['Population (1 mi)', num(demo.population_1mi)]);
      if (demo.population_3mi) marketRows.push(['Population (3 mi)', num(demo.population_3mi)]);
      if (demo.population_5mi) marketRows.push(['Population (5 mi)', num(demo.population_5mi)]);
      if (demo.median_hh_income_1mi) marketRows.push(['Median HH Income (1 mi)', `$${num(demo.median_hh_income_1mi)}`]);
      if (demo.median_hh_income_3mi) marketRows.push(['Median HH Income (3 mi)', `$${num(demo.median_hh_income_3mi)}`]);
      if (demo.median_hh_income_5mi) marketRows.push(['Median HH Income (5 mi)', `$${num(demo.median_hh_income_5mi)}`]);
      // Legacy single-radius field names from earlier extractions.
      if (demo.population) marketRows.push(['Population', String(demo.population)]);
      if (demo.median_income) marketRows.push(['Median HH Income', String(demo.median_income)]);
    }

    if (marketRows.length > 0) {
      yPos = section('MARKET CONTEXT', yPos);
      table({
        startY: yPos,
        head: [[headCell('Location Factor'), headCell('Detail')]],
        body: marketRows,
        theme: 'striped',
        headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
        alternateRowStyles: { fillColor: CORPUS.stripe },
        styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0, overflow: 'linebreak' },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.34, fontStyle: boldStyle },
          1: { cellWidth: contentWidth * 0.66 },
        },
        margin: tableMargins,
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // CURRENT VS. PRO FORMA (STABILIZED)
  //
  // Only for deals with a stabilization story - see showProForma above. The
  // "Current" column is the same NOI and cap the hero strip printed, not a
  // second set of figures pulled from the property record.
  // ══════════════════════════════════════════════════════════════════════

  if (showProForma && proForma) {
    if (yPos > 215) { doc.addPage(); yPos = TOP; }
    yPos = section('CURRENT VS. PRO FORMA (STABILIZED)', yPos);

    const delta = (current: number | null | undefined, target: number | null | undefined,
                   fmt: (v: number) => string): string => {
      if (target == null || !isFinite(target) || current == null || !isFinite(current)) return '-';
      const d = target - current;
      return `${d >= 0 ? '+' : '-'}${fmt(Math.abs(d))}`;
    };

    const pfBody: string[][] = [
      ['NOI', formatCurrency(noi), formatCurrency(proForma.noi), delta(noi, proForma.noi, formatCurrency)],
      ['Cap Rate', formatPercent(capRate), proForma.cap_rate ? formatPercent(proForma.cap_rate) : '-',
        delta(capRate, proForma.cap_rate, v => `${v.toFixed(2)}%`)],
      ['Gross Income', formatCurrency(pgi), formatCurrency(proForma.gross_income),
        delta(pgi, proForma.gross_income, formatCurrency)],
      ['Occupancy', property.occupancy_rate != null ? `${property.occupancy_rate}%` : '-',
        proForma.occupancy_rate != null ? `${proForma.occupancy_rate}%` : '-',
        delta(property.occupancy_rate, proForma.occupancy_rate, v => `${v.toFixed(0)}%`)],
    ];

    table({
      startY: yPos,
      head: [[
        headCell('Metric'), headCell('Current (In-Place)', 'right'),
        headCell('Pro Forma (Stabilized)', 'right'), headCell('Delta', 'right'),
      ]],
      body: pfBody,
      theme: 'striped',
      headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8, fontStyle: boldStyle, cellPadding: 2.8 },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.25 },
        1: { cellWidth: contentWidth * 0.25, halign: 'right' },
        2: { cellWidth: contentWidth * 0.27, halign: 'right', fontStyle: boldStyle },
        3: { cellWidth: contentWidth * 0.23, halign: 'right', textColor: CORPUS.green },
      },
      margin: tableMargins,
    });
    yPos = (doc as any).lastAutoTable.finalY + 5;

    yPos = note(
      proForma.notes
        ? `Pro forma basis: ${proForma.notes}`
        : 'Pro forma figures are the offering memorandum\'s stabilized case. The returns in this '
          + 'analysis are priced on the in-place column unless the exit is explicitly stabilized.',
      yPos
    );
    yPos += 2;
  }

  // ══════════════════════════════════════════════════════════════════════
  // VALUE-ADD ANALYSIS
  //
  // Only for deals with levers set or real value-add dollars budgeted, and
  // never for stnl_nnn / ground_lease - see showValueAdd above.
  // ══════════════════════════════════════════════════════════════════════

  if (showValueAdd) {
    if (yPos > 210) { doc.addPage(); yPos = TOP; }
    yPos = section('VALUE-ADD ANALYSIS', yPos);

    if (activeLevers.length > 0) {
      table({
        startY: yPos,
        head: [[headCell('Value-Add Lever'), headCell('Basis')]],
        body: activeLevers.map(l => [l.label, l.note?.trim() || 'Identified']),
        theme: 'striped',
        headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
        alternateRowStyles: { fillColor: CORPUS.stripe },
        styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0, overflow: 'linebreak' },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.34, fontStyle: boldStyle },
          1: { cellWidth: contentWidth * 0.66 },
        },
        margin: tableMargins,
      });
      yPos = (doc as any).lastAutoTable.finalY + 6;
    }

    // The as-is / stabilized worksheet is its own set of inputs on the deal
    // record. It is NOT a second derivation of the deal's NOI - the analysis
    // above continues to price off the single NOI computed once at the top -
    // so it is only printed when those inputs were actually filled in.
    const asIsIncome = sqft * n(data.as_is_rent_psf) * (n(data.as_is_occupancy) / 100) + n(data.as_is_other_income);
    const stabilizedIncome = sqft * n(data.stabilized_rent_psf) * (n(data.stabilized_occupancy) / 100)
      + n(data.stabilized_other_income);
    const asIsWorksheetNOI = asIsIncome - asIsIncome * (n(data.as_is_expense_ratio) / 100);
    const stabilizedWorksheetNOI = stabilizedIncome - stabilizedIncome * (n(data.stabilized_expense_ratio) / 100);
    const hasWorksheet = n(data.as_is_rent_psf) > 0 || n(data.stabilized_rent_psf) > 0
      || n(data.as_is_occupancy) > 0 || n(data.stabilized_occupancy) > 0;

    if (hasWorksheet) {
      table({
        startY: yPos,
        head: [[
          headCell('Metric'), headCell('As-Is', 'right'),
          headCell('Stabilized', 'right'), headCell('Delta', 'right'),
        ]],
        body: [
          ['Rent PSF', `$${n(data.as_is_rent_psf).toFixed(2)}`, `$${n(data.stabilized_rent_psf).toFixed(2)}`,
            `+$${(n(data.stabilized_rent_psf) - n(data.as_is_rent_psf)).toFixed(2)}`],
          ['Occupancy', `${n(data.as_is_occupancy).toFixed(1)}%`, `${n(data.stabilized_occupancy).toFixed(1)}%`,
            `+${(n(data.stabilized_occupancy) - n(data.as_is_occupancy)).toFixed(1)}%`],
          ['NOI (worksheet)', formatCurrency(asIsWorksheetNOI), formatCurrency(stabilizedWorksheetNOI),
            `+${formatCurrency(stabilizedWorksheetNOI - asIsWorksheetNOI)}`],
        ],
        theme: 'striped',
        headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8, fontStyle: boldStyle, cellPadding: 2.8 },
        alternateRowStyles: { fillColor: CORPUS.stripe },
        styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.28 },
          1: { cellWidth: contentWidth * 0.24, halign: 'right' },
          2: { cellWidth: contentWidth * 0.24, halign: 'right', fontStyle: boldStyle },
          3: { cellWidth: contentWidth * 0.24, halign: 'right', textColor: CORPUS.green },
        },
        margin: tableMargins,
      });
      yPos = (doc as any).lastAutoTable.finalY + 6;
    }

    if (vaTotalCost > 0) {
      yPos = moneyTable(['Value-Add Budget', 'Amount'], [
        ['CapEx', formatCurrency(n(data.va_capex))],
        ['TI / Leasing', formatCurrency(n(data.va_ti_leasing))],
        ['Carry Costs', formatCurrency(n(data.va_carry_costs))],
        ['Contingency', formatCurrency(n(data.va_contingency))],
        redRow('Total Value-Add Costs', formatCurrency(vaTotalCost)),
      ], yPos) + 5;
      yPos = note(
        `The ${formatCurrency(vaTotalCost)} budget is already inside the `
        + `${formatCurrency(totalCashInvested)} total cash required and the returns above.`,
        yPos
      );
    }
    yPos += 4;
  }

  // ══════════════════════════════════════════════════════════════════════
  // N-YEAR PROFORMA
  // ══════════════════════════════════════════════════════════════════════

  yPos = section(`${holdingPeriod}-YEAR PROFORMA`, yPos);

  table({
    startY: yPos,
    head: [[
      headCell('Year', 'center'), headCell('NOI', 'right'), headCell('Debt Service', 'right'),
      headCell('Cash Flow', 'right'), headCell('CoC %', 'right'),
    ]],
    body: projections.map(yr => [
      `${yr.year}`,
      formatCurrency(yr.noi),
      formatCurrency(yr.debtService),
      formatCurrency(yr.cashFlow),
      formatPercent(calculateCashOnCash(yr.cashFlow, totalCashInvested)),
    ]),
    theme: 'striped',
    headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
    alternateRowStyles: { fillColor: CORPUS.stripe },
    styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.16, halign: 'center' },
      1: { cellWidth: contentWidth * 0.21, halign: 'right' },
      2: { cellWidth: contentWidth * 0.21, halign: 'right' },
      3: { cellWidth: contentWidth * 0.21, halign: 'right' },
      4: { cellWidth: contentWidth * 0.21, halign: 'right' },
    },
    margin: tableMargins,
  });
  yPos = (doc as any).lastAutoTable.finalY + 10;

  // ══════════════════════════════════════════════════════════════════════
  // EXIT ANALYSIS
  // ══════════════════════════════════════════════════════════════════════

  yPos = section(`EXIT ANALYSIS (YEAR ${holdingPeriod})`, yPos);

  const exitBody: any[] = [
    [`Exit Year NOI (${exitNOIYearLabel})`, formatCurrency(exitNOI)],
    ['Exit Cap Rate', formatPercent(exitCapRate)],
    ['Gross Sale Price', formatCurrency(saleProceeds.salePrice)],
    [`Less: Selling Costs (${formatPercent(sellingCostsPercent)})`, `(${formatCurrency(saleProceeds.sellingCosts)})`],
    ['Net Sale Proceeds', formatCurrency(saleProceeds.salePrice - saleProceeds.sellingCosts)],
  ];
  /**
   * ⛔ EACH NOTE AT ITS EXIT-YEAR BALANCE, taken from the projection. This block printed the
   * bank's balance and then subtracted the carryback's ORIGINAL PRINCIPAL a SECOND time from a
   * net that had already retired it - so the document's Net Equity Proceeds disagreed with the
   * screen's by the whole carryback. Two chains, one deal.
   */
  const exitBankBalance = lastYear?.bankLoanBalance ?? loanAmount;
  const exitSecondBalance = lastYear?.secondaryLoanBalance ?? 0;
  exitBody.push([
    exitSecondBalance > 0 ? 'Less: Bank Loan Payoff (Year 5 balance)' : 'Less: Loan Payoff',
    `(${formatCurrency(exitBankBalance)})`,
  ]);
  if (exitSecondBalance > 0) {
    exitBody.push(['Less: Second Note Payoff (Year 5 balance)', `(${formatCurrency(exitSecondBalance)})`]);
  }
  exitBody.push(boldRow('Net Equity Proceeds', formatCurrency(saleProceeds.netToSeller)));
  yPos = moneyTable(['Line Item', 'Amount'], exitBody, yPos) + 5;

  // Keep the returns block with its own header rather than stranding the head
  // row at the foot of a page.
  if (yPos > 228) { doc.addPage(); yPos = TOP; }
  yPos = moneyTable(['Metric', 'Value'], [
    ['IRR', formatPercent(irrValue)],
    ['Equity Multiple', `${equityMultiple.toFixed(2)}x`],
    ['Avg Cash-on-Cash', formatPercent(avgCashOnCash)],
    [`Total Profit (${holdingPeriod}-Year)`, formatCurrency(totalProfit)],
  ], yPos) + 10;

  // ══════════════════════════════════════════════════════════════════════
  // DECISION SCORECARD
  // ══════════════════════════════════════════════════════════════════════

  if (yPos > 200) { doc.addPage(); yPos = TOP; }
  yPos = section(`DECISION SCORECARD - ${profile.toUpperCase()}`, yPos);

  doc.setFillColor(...(isGo ? CORPUS.greenFill : isReview ? CORPUS.amberFill : CORPUS.redFill));
  doc.setDrawColor(...(isGo ? CORPUS.greenEdge : isReview ? CORPUS.amberEdge : CORPUS.redEdge));
  doc.setLineWidth(0.5);
  doc.rect(margin, yPos, contentWidth, 18, 'FD');
  doc.setTextColor(...(isGo ? CORPUS.green : isReview ? CORPUS.amber : CORPUS.red));
  doc.setFontSize(20);
  doc.setFont(family, 'normal');
  doc.text(verdict, pageWidth / 2, yPos + 12.5, { align: 'center' });
  yPos += 22;

  table({
    startY: yPos,
    head: [['Metric', 'Threshold', 'Actual', 'Status']],
    // An inapplicable metric prints UNLEVERED with N/A actual, exactly as his delivered
    // analyses do - all six print five rows, and the three all-cash deals show the DSCR line
    // as "N/A  UNLEVERED". Printing 0.00x FAIL there would read as a miss the deal never had.
    body: rows.map(r => [
      r.label,
      `>= ${fmtScore(r.threshold, r.format)}`,
      r.applicable ? fmtScore(r.actual, r.format) : 'N/A',
      r.applicable ? (r.pass ? 'PASS' : 'FAIL') : 'UNLEVERED',
    ]),
    theme: 'striped',
    headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
    alternateRowStyles: { fillColor: CORPUS.stripe },
    styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
    columnStyles: {
      0: { cellWidth: contentWidth * 0.30 },
      1: { cellWidth: contentWidth * 0.22 },
      2: { cellWidth: contentWidth * 0.22 },
      3: { cellWidth: contentWidth * 0.26 },
    },
    didParseCell: (hookData: any) => {
      if (hookData.section === 'body' && hookData.column.index === 3) {
        hookData.cell.styles.textColor =
          hookData.cell.raw === 'PASS' ? CORPUS.green
          : hookData.cell.raw === 'UNLEVERED' ? CORPUS.ink
          : CORPUS.red;
        hookData.cell.styles.fontStyle = boldStyle;
      }
    },
    margin: tableMargins,
  });
  yPos = (doc as any).lastAutoTable.finalY + 4;

  // The definitions, in small italic type under the scorecard, so the status reads as a
  // classification rather than an opinion. His six analyses all carry this block once.
  doc.setFontSize(6.4);
  doc.setFont(family, 'italic');
  doc.setTextColor(120, 126, 136);
  const defs = 'Status definitions - ' +
    (Object.keys(VERDICT_DEFINITIONS) as Verdict[]).map(v => `${v}: ${VERDICT_DEFINITIONS[v]}.`).join(' ');
  const defLines: string[] = doc.splitTextToSize(defs, contentWidth);
  doc.text(defLines, margin, yPos);
  yPos += defLines.length * 2.6 + 2;
  doc.setFontSize(7);
  doc.setFont(family, 'normal');
  doc.setTextColor(70, 76, 88);
  const criteria = 'Metrics are measured against the investment criteria provided.';
  doc.text(criteria, margin, yPos);
  yPos += 4;

  // Open items are what separates CONDITIONAL from GO, so the document has to name them.
  // Same list page 1 printed - one filter, so the two can never disagree about what is open.
  if (listedItems.length > 0) {
    const items = 'Items to verify: ' + listedItems.join('; ');
    const itemLines: string[] = doc.splitTextToSize(items, contentWidth);
    if (yPos + itemLines.length * 3.4 > 268) { doc.addPage(); yPos = TOP; }
    doc.text(itemLines, margin, yPos);
    yPos += itemLines.length * 3.4 + 2;
  }
  yPos += 6;

  // ══════════════════════════════════════════════════════════════════════
  // OPTIMIZATION - WHAT MAKES THIS A GO?
  //
  // Goal-seek: the price, NOI, rent, capex and exit cap that would each on
  // their own carry the deal to the target IRR. Every "Current" figure below
  // is the one already printed above - the sized loan and its LTV (not
  // data.ltv_percent), the NOI in the hero strip, the cash in the financing
  // table - so the Gap column can never argue with the rest of the document.
  // ══════════════════════════════════════════════════════════════════════

  if (purchasePrice > 0 && egi > 0) {
    if (yPos > 200) { doc.addPage(); yPos = TOP; }
    yPos = section('OPTIMIZATION - WHAT MAKES THIS A GO?', yPos);

    // goalSeekInputs, targetIRR and maxPurchasePrice are hoisted above PAGE 1 - see the note there.
    const requiredNOI = solveRequiredNOILift(targetIRR, goalSeekInputs);
    const capexCeiling = solveCapexCeiling(targetIRR, goalSeekInputs);
    const targetExitCap = solveTargetExitCap(targetIRR, goalSeekInputs);
    const requiredRentPSF = requiredNOI > 0
      ? solveRequiredRentPSF(requiredNOI, sqft, (totalExpenses / (egi || 1)) * 100)
      : 0;

    const orNA = (val: number, fmt: (v: number) => string) => (val === 0 ? 'Not achievable' : fmt(val));
    // formatCurrency renders a negative as "$-159,076"; the sign belongs in
    // front of the symbol on a gap column that is read at a glance.
    const signed = (delta: number, fmt: (v: number) => string) =>
      `${delta < 0 ? '-' : '+'}${fmt(Math.abs(delta))}`;

    const optRows: string[][] = [
      ['Max Purchase Price', formatCurrency(purchasePrice), orNA(maxPurchasePrice, formatCurrency),
        maxPurchasePrice === 0 ? '-' : signed(maxPurchasePrice - purchasePrice, formatCurrency)],
      ['Required NOI', formatCurrency(noi), orNA(requiredNOI, formatCurrency),
        requiredNOI === 0 ? '-' : signed(requiredNOI - noi, formatCurrency)],
    ];
    if (sqft > 0) {
      const currentRentPSF = pgi / sqft;
      optRows.push(['Required Rent PSF', `$${currentRentPSF.toFixed(2)}/SF`,
        requiredRentPSF === 0 ? 'Not achievable' : `$${requiredRentPSF.toFixed(2)}/SF`,
        requiredRentPSF === 0 ? '-' : signed(requiredRentPSF - currentRentPSF, v => `$${v.toFixed(2)}/SF`)]);
    }
    optRows.push(
      ['CapEx Ceiling', formatCurrency(vaTotalCost), orNA(capexCeiling, formatCurrency),
        capexCeiling === 0 ? 'No room' : signed(capexCeiling - vaTotalCost, formatCurrency)],
      ['Target Exit Cap', formatPercent(exitCapRate), orNA(targetExitCap, formatPercent),
        targetExitCap === 0 ? '-' : signed(targetExitCap - exitCapRate, v => `${v.toFixed(2)}%`)],
    );

    table({
      startY: yPos,
      head: [[
        headCell('Lever'), headCell('Current', 'right'),
        headCell(`Required for ${formatPercent(targetIRR)} IRR`, 'right'), headCell('Gap', 'right'),
      ]],
      body: optRows,
      theme: 'striped',
      headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8, fontStyle: boldStyle, cellPadding: 2.8 },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.26, fontStyle: boldStyle },
        1: { cellWidth: contentWidth * 0.22, halign: 'right' },
        2: { cellWidth: contentWidth * 0.30, halign: 'right', fontStyle: boldStyle },
        3: { cellWidth: contentWidth * 0.22, halign: 'right' },
      },
      margin: tableMargins,
    });
    yPos = (doc as any).lastAutoTable.finalY + 5;

    yPos = note(
      `Target IRR ${formatPercent(targetIRR)} (${profile}). Each lever is solved on its own with the `
      + 'others held at the values above.',
      yPos
    );
    yPos += 4;
  }

  // ══════════════════════════════════════════════════════════════════════
  // SENSITIVITY: EXIT CAP RATE
  // ══════════════════════════════════════════════════════════════════════

  // ⭐⭐⭐ KK2. NO BASE OF ITS OWN. `scenarioBase` is the screen's `scenarioBaseResolved`, handed in
  // as an option, so the sensitivity table and the scenario page price the SAME deal the on-screen
  // scenario cards and scenario builder price - second note, fee and stated exit value included.
  // Building one here is how the document came to retire no note and pay no fee.

  if (purchasePrice > 0 && exitCapRate > 0) {
    // The scenario base carries the screen's exit NOI and contractual NOI
    // schedule, so every row below prices its exit exactly as the verdict card
    // and the scenario cards do.

    if (yPos > 205) { doc.addPage(); yPos = TOP; }
    yPos = section('SENSITIVITY: EXIT CAP RATE', yPos);
    yPos = caption(
      `At ${formatCurrency(downPayment)} down (${ltvPercent.toFixed(1)}% LTV at ${formatCurrency(purchasePrice)}); `
      + `${exitNOIYearLabel} NOI of ${formatCurrency(exitNOI)}.`,
      yPos
    );

    const capLadder = [-0.5, 0, 0.5, 1.0, 1.5, 2.0]
      .map(delta => exitCapRate + delta)
      .filter(cap => cap > 0);

    const capRows = capLadder.map(cap => {
      const r = computeScenario({ ...scenarioBase, exitCapRate: cap }, 0, 0);
      const grossSale = exitNOI / (cap / 100);
      const netEquity = grossSale - grossSale * (sellingCostsPercent / 100) - loanBalanceAtExit;
      return [
        formatPercent(cap),
        formatCurrency(grossSale),
        formatCurrency(netEquity),
        formatPercent(r.irr),
        `${r.equityMultiple.toFixed(2)}x`,
      ];
    });
    const baseRowIndex = capLadder.findIndex(c => Math.abs(c - exitCapRate) < 0.001);

    table({
      startY: yPos,
      head: [[
        headCell('Exit Cap'), headCell('Gross Sale', 'right'), headCell('Net Equity', 'right'),
        headCell('IRR', 'right'), headCell('EM', 'right'),
      ]],
      body: capRows,
      theme: 'striped',
      headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0, halign: 'right' },
      columnStyles: { 0: { halign: 'left' } },
      didParseCell: (hookData: any) => {
        if (hookData.section === 'body' && hookData.row.index === baseRowIndex) {
          hookData.cell.styles.fontStyle = boldStyle;
        }
      },
      margin: tableMargins,
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  // ══════════════════════════════════════════════════════════════════════
  // SENSITIVITY: PURCHASE PRICE
  // ══════════════════════════════════════════════════════════════════════

  if (purchasePrice > 0 && noi > 0) {
    if (yPos > 205) { doc.addPage(); yPos = TOP; }
    yPos = section('SENSITIVITY: PURCHASE PRICE', yPos);
    yPos = caption(
      `Constant ${formatCurrency(downPayment)} cash equity across scenarios; loan size varies `
      + `(loan = price - equity${sellerCarrybackAmt > 0 ? ' - seller carryback' : ''}). `
      + `Closing costs at ${n(data.closing_costs_percent)}% of price; `
      + `${n(data.interest_rate).toFixed(2)}% / ${n(data.amortization_years)}-yr amortization.`,
      yPos
    );

    const priceRows = [-10, -5, 0, 5, 10].map(mod => {
      const price = purchasePrice * (1 + mod / 100);
      const loan = Math.max(0, price - downPayment - sellerCarrybackAmt);
      const mPmt = calculateMonthlyPayment(loan, n(data.interest_rate), n(data.amortization_years));
      const ds = calculateAnnualDebtService(mPmt) + sellerAnnualDS;
      const closing = calculateClosingCosts(price, n(data.closing_costs_percent));
      const cash = downPayment + closing + vaTotalCost;
      const cf = calculateBeforeTaxCashFlow(yearOneNOI, ds);
      return [
        `${mod >= 0 ? '+' : ''}${mod}%`,
        formatCurrency(price),
        formatCurrency(loan),
        formatPercent(calculateCapRate(noi, price)),
        `${calculateDSCR(yearOneNOI, ds).toFixed(2)}x`,
        formatPercent(calculateCashOnCash(cf, cash)),
      ];
    });

    table({
      startY: yPos,
      head: [[
        headCell('Scenario'), headCell('Price', 'right'), headCell('Loan', 'right'),
        headCell(capCaption.goingInCap, 'right'), headCell('DSCR Y1', 'right'), headCell('Cash-on-Cash Y1', 'right'),
      ]],
      body: priceRows,
      theme: 'striped',
      headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8, fontStyle: boldStyle, cellPadding: 2.8 },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 8.2, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0, halign: 'right' },
      columnStyles: { 0: { halign: 'left' } },
      didParseCell: (hookData: any) => {
        if (hookData.section === 'body' && hookData.row.index === 2) {
          hookData.cell.styles.fontStyle = boldStyle;
        }
      },
      margin: tableMargins,
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;
  }

  // ══════════════════════════════════════════════════════════════════════
  // SCENARIO - <NAME>
  //
  // ⭐⭐ HIS HOUSE SECTION, and five of the six delivered analyses carry one: "SCENARIO - TENANT
  // OR LICENSE FAILURE", "SCENARIO - NO RENEWAL OPTIONS ON EITHER LEASE". The app had no
  // equivalent because it could not know WHICH risk to name - that is a judgement about the deal.
  // Joseph now types it and the model turns it into levers.
  //
  // ⛔ EVERY FIGURE ON THIS PAGE IS `runScenario`, on the SAME `scenarioBase` the sensitivity
  // tables above use. The model contributed the levers and the words; it did not contribute one
  // number. ⭐ The assumptions PRINT beside the result - an assumption you can see is one you can
  // correct, and choosing 12 months of downtime instead of 6 moves the IRR materially.
  // ══════════════════════════════════════════════════════════════════════

  if (scenario && scenario.assumptions.length > 0 && purchasePrice > 0 && egi > 0) {
    const outcome = runScenario(scenarioBase, scenario.assumptions);
    if (yPos > 190) { doc.addPage(); yPos = TOP; }
    yPos = section(scenario.title || 'SCENARIO', yPos);

    doc.setFontSize(7.6);
    doc.setFont(family, 'normal');
    doc.setTextColor(70, 76, 88);
    const assumeLines: string[] = doc.splitTextToSize(
      'Modelled as: ' + outcome.applied.map(a =>
        `${a.label}${a.year ? ` (year ${a.year})` : ''}`).join('; ') + '.', contentWidth);
    doc.text(assumeLines, margin, yPos);
    yPos += assumeLines.length * 3.5 + 3;

    table({
      startY: yPos,
      head: [['Metric', 'Base Case', 'Scenario']],
      body: [
        ['IRR', formatPercent(outcome.base.irr), formatPercent(outcome.modelled.irr)],
        ['Equity Multiple', formatRatio(outcome.base.equityMultiple), formatRatio(outcome.modelled.equityMultiple)],
        ['Avg Cash-on-Cash', formatPercent(outcome.base.avgCoC), formatPercent(outcome.modelled.avgCoC)],
        ['Year 1 NOI', formatCurrency(outcome.base.noi), formatCurrency(outcome.modelled.noi)],
        ['Year 1 Cash Flow', formatCurrency(outcome.base.cashFlow), formatCurrency(outcome.modelled.cashFlow)],
      ],
      theme: 'striped',
      headStyles: { fillColor: CORPUS.navy, textColor: CORPUS.white, fontSize: 8.5, fontStyle: boldStyle, cellPadding: 2.8 },
      alternateRowStyles: { fillColor: CORPUS.stripe },
      styles: { fontSize: 8.5, cellPadding: 2.8, textColor: CORPUS.ink, lineWidth: 0 },
      columnStyles: { 0: { cellWidth: contentWidth * 0.4 }, 1: { cellWidth: contentWidth * 0.3 }, 2: { cellWidth: contentWidth * 0.3 } },
      margin: tableMargins,
    });
    yPos = (doc as any).lastAutoTable.finalY + 6;

    if (scenario.rationale) {
      const rat: string[] = doc.splitTextToSize(scenario.rationale, contentWidth);
      if (yPos + rat.length * 3.5 > 262) { doc.addPage(); yPos = TOP; }
      doc.setFontSize(7.8);
      doc.setTextColor(70, 76, 88);
      doc.text(rat, margin, yPos);
      yPos += rat.length * 3.5 + 4;
    }

    // ⛔ WHAT THE RECORD COULD NOT ESTABLISH, IN PRINT. Joseph's instruction: never invent, say
    // you do not have it. A gap the reader cannot see is one they will assume was checked.
    const gaps = (scenario.notEstablished ?? []).filter(Boolean);
    if (gaps.length > 0) {
      const gl: string[] = doc.splitTextToSize('Not established by the record: ' + gaps.join('; ') + '.', contentWidth);
      if (yPos + gl.length * 3.4 > 264) { doc.addPage(); yPos = TOP; }
      doc.setFontSize(7);
      doc.setFont(family, 'italic');
      doc.setTextColor(120, 126, 136);
      doc.text(gl, margin, yPos);
      doc.setFont(family, 'normal');
      yPos += gl.length * 3.4 + 6;
    }
    if (outcome.ignored.length > 0) {
      doc.setFontSize(7);
      doc.setTextColor(...CORPUS.amber);
      doc.text('Not modelled: ' + outcome.ignored.map(i => i.label).join('; ') + '.', margin, yPos);
      yPos += 6;
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // KEY CONSIDERATIONS
  // ══════════════════════════════════════════════════════════════════════

  if (keyConsiderations) {
    if (yPos > 200) { doc.addPage(); yPos = TOP; }
    yPos = section('KEY CONSIDERATIONS', yPos);

    const paragraphs = keyConsiderations.split('\n').filter(l => l.trim() !== '');
    for (const raw of paragraphs) {
      const para = raw.replace(/^#{1,6}\s*/, '').trim();
      const headingMatch = para.match(/^\*\*(.+?)\*\*:?$/);
      if (headingMatch) {
        if (yPos > 262) { doc.addPage(); yPos = TOP; }
        doc.setFontSize(9.5);
        doc.setFont(family, 'normal');
        doc.setTextColor(...CORPUS.ink);
        doc.text(headingMatch[1], margin, yPos);
        yPos += 4.6;
        continue;
      }

      const clean = para.replace(/\*\*/g, '');
      doc.setFontSize(7.8);
      doc.setFont(family, 'normal');
      doc.setTextColor(70, 76, 88);
      const wrapped: string[] = doc.splitTextToSize(clean, contentWidth);
      const blockHeight = wrapped.length * 3.5 + 3.5;
      if (yPos + blockHeight > 268) { doc.addPage(); yPos = TOP; }
      doc.text(wrapped, margin, yPos);
      yPos += blockHeight;
    }
    yPos += 4;
  }

  // ══════════════════════════════════════════════════════════════════════
  // AI ADVISOR INSIGHTS (appendix - only when the caller starred any)
  // ══════════════════════════════════════════════════════════════════════

  if (aiInsights && aiInsights.length > 0) {
    if (yPos > 200) { doc.addPage(); yPos = TOP; }
    yPos = section('AI ADVISOR INSIGHTS', yPos);
    yPos = caption('Starred insights from the AI Deal Advisor.', yPos) + 2;

    for (const insight of aiInsights) {
      const cleaned = insight.content
        .replace(/\*\*/g, '')
        .replace(/```[\s\S]*?```/g, '[code block]')
        .replace(/`([^`]+)`/g, '$1');
      const lines: string[] = doc.splitTextToSize(cleaned, contentWidth - 8);
      const capped = lines.slice(0, 20);
      const boxHeight = capped.length * 3.8 + 5;
      if (yPos + boxHeight + 8 > 268) { doc.addPage(); yPos = TOP; }

      doc.setFontSize(6.8);
      doc.setTextColor(...CORPUS.slate);
      doc.text(
        new Date(insight.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        margin,
        yPos
      );
      yPos += 3.6;

      doc.setFillColor(...CORPUS.stripe);
      doc.setDrawColor(...CORPUS.rule);
      doc.setLineWidth(0.3);
      doc.rect(margin, yPos - 3, contentWidth, boxHeight, 'FD');
      doc.setFontSize(7.8);
      doc.setTextColor(70, 76, 88);
      doc.text(capped, margin + 4, yPos + 1);
      yPos += boxHeight + 5;
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // Disclaimer
  // ══════════════════════════════════════════════════════════════════════

  // ══════════════════════════════════════════════════════════════════════
  // PREPARED BY - the specimen's last page: the two marks, both brokers, the firm's line
  // ══════════════════════════════════════════════════════════════════════

  if (yPos > 205) { doc.addPage(); yPos = TOP; }
  yPos = section('PREPARED BY', yPos);
  if (apexMark) { try { doc.addImage(apexMark, 'AUTO', margin, yPos, 22, 22 * (690 / 1080)); } catch { /* decorative */ } }
  if (kwMark) { try { doc.addImage(kwMark, 'AUTO', margin + 1.5, yPos + 17, 19, 19 * (181 / 275)); } catch { /* decorative */ } }
  const pbX = margin + 32;
  const pbColW = (contentWidth - 32) / 2;
  PREPARED_BY.forEach((b, i) => {
    const x = pbX + i * pbColW;
    let y = yPos + 3.5;
    doc.setFontSize(10);
    doc.setFont(family, boldStyle);
    doc.setTextColor(...CORPUS.ink);
    doc.text(b.name, x, y);
    y += 4.8;
    doc.setFontSize(8);
    doc.setFont(family, 'normal');
    doc.setTextColor(...CORPUS.slate);
    for (const line of [b.title, 'Apex Real Estate Services / KW Commercial', b.dre]) { doc.text(line, x, y); y += 3.9; }
    doc.setFont(family, boldStyle);
    doc.setTextColor(...CORPUS.ink);
    doc.text(b.phone, x, y);
    y += 3.9;
    doc.setFont(family, 'normal');
    doc.setTextColor(...CORPUS.slate);
    doc.text(b.email, x, y);
    y += 3.9;
    doc.text('apex-res.com', x, y);
  });
  yPos += 36;
  doc.setFontSize(7.5);
  doc.setFont(family, 'normal');
  doc.setTextColor(...CORPUS.slate);
  doc.text(`${firmName}  ·  ${FIRM_ADDRESS_LINE}  ·  apex-res.com`, margin, yPos);
  yPos += 7;

  if (yPos > 248) { doc.addPage(); yPos = TOP; }
  doc.setFontSize(6);
  doc.setFont(family, 'normal');
  doc.setTextColor(150, 155, 165);
  const disclaimer = 'This analysis is prepared for informational purposes only and does not constitute an offer, '
    + 'solicitation, or recommendation to buy or sell any property. All projections are estimates based on assumptions '
    + 'that may not materialize. Actual results may vary materially. Buyers should conduct their own independent due '
    + 'diligence including inspection, title review, environmental assessment, and financial verification prior to acquisition.';
  doc.text(doc.splitTextToSize(disclaimer, contentWidth), margin, yPos + 4);

  // ══════════════════════════════════════════════════════════════════════
  // Header band and footer on every page
  //
  // Drawn last so pages that autoTable created mid-flow are covered too.
  // ══════════════════════════════════════════════════════════════════════

  const footerLeft = `${firmName}  |  ${FOOTER_CONTACTS}  |  Confidential`;
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawMasthead(doc, family, frame, firmName, headerDate);
    drawFooterLine(doc, family, frame, footerLeft, i);
  }
  // The specimen's page 1 alone carries the marks under the bar, with the firm line and the
  // document's line at right - "Prepared for Vinod & Nayana Kardani · Confidential Investment Analysis".
  doc.setPage(1);
  drawLogoRow(doc, family, frame, BAND_H + 3, { apex: apexMark, kw: kwMark },
    `${profile ? `Prepared for ${profile} · ` : ''}Confidential Investment Analysis`);

  // Saved to his machine FIRST, then filed - the download never waits on, or fails with, the filing.
  const fileName = exportFileName('Investment Analysis', property.address);
  doc.save(fileName);
  return fileExportedPdf(doc, { kind: 'Investment Analysis', fileName, propertyId: property.id, dealId: dealIdOf(property) });
}
